import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
    Card,
    CardHeader,
    CardBody,
    CardTitle,
    Row,
    Col,
    Table,
    Badge,
    Button,
    Spinner,
    Modal,
    Form,
    Alert,
    Tabs,
    Tab,
    OverlayTrigger,
    Tooltip,
} from 'react-bootstrap';
import { MdVolumeUp, MdVolumeOff, MdDirections, MdMap, MdStorefront, MdWarning, MdPerson, MdPhone, MdLocationOn, MdLocalShipping, MdTwoWheeler, MdInventory, MdAutorenew, MdFlashOn, MdFlashOff, MdSettings, MdTune, MdSave, MdClose, MdFilterList, MdCalendarToday, MdSort, MdClear } from 'react-icons/md';
import { Chip } from '@material-ui/core';
import axios from 'axios';
import { toast } from 'react-toastify';
import { getUserFromLocalStorage } from '../../utilities/user';
import { socket } from '../../utilities/socket';
import useOrderAlerts from '../../hooks/useOrderAlerts';
import notificationSound from '../../assets/img/newOrderNotification.mp3';
import recallSound from '../../assets/img/restaurant_notification_beep.mp3';
import { colors, font, button, pill } from './partnerTheme';
import ConfirmDialog, { useConfirmDialog } from '../../components/ConfirmDialog';
import { apiAcceptPartnerOffer, apiDeclinePartnerOffer } from '../../api/index.js';
import PartnerHelp from './partnerHelp';
import AppSelect from "../../components/ui/AppSelect";

// The tabs of the orders card, as the stat cards above it name them.
const ORDER_TABS = ['all', 'pending', 'inTransit', 'completed'];

const API_URL = process.env.REACT_APP_SERVER_URL || '';

const statusColors = {
    PROCESSING: '#ff9800',
    PENDING_3PL: '#ff9800',
    ASSIGNED: '#3f51b5',
    ACCEPTED: '#673ab7',
    PICKED_UP: '#2196f3',
    DISPATCHED: '#00bcd4',
    'IN_TRANSIT': '#2196f3',
    ARRIVED: '#8bc34a',
    DELIVERED: '#4caf50',
    CANCELLED: '#f44336',
    REJECTED: '#9e9e9e',
};

const PartnerOrders = () => {
    const { agent } = useSelector((state) => state.auth);
    const user = agent || getUserFromLocalStorage();
    const partnerId = user?.result?.partnerId || user?.result?._id;
    const token = user?.token;

    // Answering an offer is the partner owner's call, or that of a staff
    // member the owner gave full order access. The server enforces exactly the
    // same rule, so this gate exists only so the UI never offers a button that
    // would come back 403.
    const operationMode = user?.result?.operationMode;
    const isPartnerAccount = operationMode === '3PL_PARTNER';
    const platformPermissionKeys = [
        ...(Array.isArray(user?.result?.permissions) ? user.result.permissions : []),
        ...(Array.isArray(user?.result?.effectivePermissions) ? user.result.effectivePermissions : []),
    ].map((permission) => typeof permission === 'string' ? permission : permission?.key).filter(Boolean);
    const canActOnOrders = isPartnerAccount ||
        user?.result?.permissions?.orders === 'full' ||
        platformPermissionKeys.some((key) => ['orders:assign', 'orders:manage'].includes(key));

    const [orders, setOrders] = useState([]);
    const [riders, setRiders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    // The partner dashboard's cards open this page on a tab (?tab=completed
    // for Completed Today; utilities/orderDeepLinks.js). The date range stays
    // on its default, today, which is the window those cards count.
    const location = useLocation();
    const [activeTab, setActiveTab] = useState(() => {
        const tab = new URLSearchParams(location.search).get('tab');
        return ORDER_TABS.includes(tab) ? tab : 'all';
    });
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedRider, setSelectedRider] = useState('');
    const [riderSearch, setRiderSearch] = useState('');
    const [assigning, setAssigning] = useState(false);
    const [forceAssign, setForceAssign] = useState(false);

    // ─── Offer response (accept / decline) ───────────────────────────
    // Only the row whose request is in flight goes quiet, so a slow accept on
    // one order does not freeze the buttons on every other offer.
    const [offerBusyId, setOfferBusyId] = useState(null);
    // The countdown is only ever read to the minute, so one shared tick for the
    // whole table is enough; a per-row per-second timer would re-render the
    // entire order list sixty times a minute to change nothing.
    const [offerClockTick, setOfferClockTick] = useState(() => Date.now());
    // A ref, not state: ConfirmDialog builds its body once when `ask` runs, so a
    // controlled input in there would keep reading the value captured then.
    const declineReasonRef = useRef('');
    const unassignReasonRef = useRef('');
    // The orders list, so a stat card can open its tab AND bring it into view.
    const ordersCardRef = useRef(null);
    const showOrdersTab = (tab) => {
        setActiveTab(tab);
        ordersCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    const confirmDialog = useConfirmDialog();
    const [unassignBusyId, setUnassignBusyId] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);
    const [showImagePreview, setShowImagePreview] = useState(false);
    const [showMapModal, setShowMapModal] = useState(false);
    const [riderFilter, setRiderFilter] = useState('all'); // 'all', 'online', 'offline'
    const [audioEnabled, setAudioEnabled] = useState(() => {
        // Load preference from localStorage (default to true)
        const saved = localStorage.getItem('3pl_audio_enabled');
        return saved === null ? true : saved === 'true';
    });

    // Shared, reliable alert engine — one instance per sound. The assignment beep
    // also drives the hidden-tab desktop notification + Web Push; the recall beep
    // is a plain sound. Both replace the old module-scope AudioContext helpers.
    const orderAlerts = useOrderAlerts({ soundUrl: notificationSound });
    const recallAlerts = useOrderAlerts({ soundUrl: recallSound });

    // Stable refs so the long-lived socket effect never re-subscribes on re-render.
    const notifyRef = useRef(orderAlerts.notify);
    notifyRef.current = orderAlerts.notify;
    const recallPlayRef = useRef(recallAlerts.play);
    recallPlayRef.current = recallAlerts.play;

    // audioReady mirrors whether alerts are enabled (the hook auto-unlocks audio
    // on the first user gesture, so there is no separate "suspended" state to poll).
    const [audioReady, setAudioReady] = useState(orderAlerts.enabled);

    // ─── Auto-Assignment Engine State ────────────────────────────────
    const [autoAssignEnabled, setAutoAssignEnabled] = useState(false);
    const [autoAssignLoading, setAutoAssignLoading] = useState(false);
    const [autoAssignActivity, setAutoAssignActivity] = useState(null); // latest activity message
    const [showAutoAssignSettings, setShowAutoAssignSettings] = useState(false);
    const [autoAssignSettings, setAutoAssignSettings] = useState({
        strategy: 'proximity',
        maxRadiusKm: 10,
        maxRetries: 3,
        cooldownSeconds: 5,
        autoRejectTimeoutSeconds: 60,
    });
    const [settingsSaving, setSettingsSaving] = useState(false);

    // ─── Order Filters ───────────────────────────────────────────────
    const [dateRange, setDateRange] = useState('today'); // 'today', 'yesterday', '7days', '30days', 'all'
    const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'PENDING_3PL', 'PROCESSING', 'ASSIGNED', 'IN_TRANSIT', 'DELIVERED', etc.
    const [sortOrder, setSortOrder] = useState('newest'); // 'newest', 'oldest'

    // Use a ref to track audioEnabled for socket handlers
    const audioEnabledRef = React.useRef(audioEnabled);
    useEffect(() => {
        audioEnabledRef.current = audioEnabled;
        // Persist preference to localStorage
        localStorage.setItem('3pl_audio_enabled', String(audioEnabled));
    }, [audioEnabled]);

    // Keep audioReady in sync with the shared engine's enabled state (the hook
    // auto-unlocks audio on the first user gesture after a reload).
    useEffect(() => {
        setAudioReady(orderAlerts.enabled);
    }, [orderAlerts.enabled]);

    // Toggle audio on/off — drives both shared engines plus this view's UI state.
    const toggleAudio = () => {
        const newState = !audioEnabled;
        setAudioEnabled(newState);

        if (newState) {
            orderAlerts.enable();
            recallAlerts.enable({ silent: true }); // avoid a second confirmation tone
            toast.success('Audio notifications enabled');
        } else {
            orderAlerts.disable();
            recallAlerts.disable();
            toast.info('Audio notifications disabled');
        }
    };

    // Play function that respects the enabled state (uses refs for socket handlers).
    // The new-order/assignment beep routes through the shared engine's notify()
    // (sound + hidden-tab desktop notice); the recall beep is a plain play().
    const playSound = (type) => {
        if (!audioEnabledRef.current) return;

        if (type === 'recall') {
            recallPlayRef.current();
        } else {
            notifyRef.current({
                title: '📦 New order assigned',
                body: 'A new order was assigned to your team. Tap to view.',
                url: '/admin/partner-orders',
                tag: 'new-order',
            });
        }
    };

    const handleImageClick = (imageUrl) => {
        setPreviewImage(imageUrl);
        setShowImagePreview(true);
    };

    useEffect(() => {
        if (partnerId && token) {
            fetchOrders();
            fetchRiders();
        } else {
            setLoading(false);
            setError('Partner ID not found');
        }
    }, [partnerId, token]);

    // Real-time order status updates via Socket.io
    useEffect(() => {
        const handleStatusChange = ({ orderId, status, riderId }) => {
            // Normalize status to uppercase for consistency
            const normalizedStatus = status?.toUpperCase();
            setOrders(prev => prev.map(order =>
                order._id === orderId ? {
                    ...order,
                    delivery_status: normalizedStatus,
                    ...(riderId === null ? {
                        riderId: null,
                        riderAcceptedOrder: false,
                        assignmentDetails: {
                            ...order.assignmentDetails,
                            riderId: null,
                            assignedRiderId: null,
                            status: normalizedStatus,
                        },
                    } : {}),
                } : order
            ));
        };

        // Handle rider accepted order (via partner room)
        const handleOrderAccepted = ({ orderId, riderId }) => {
            setOrders(prev => prev.map(order =>
                order._id === orderId ? { ...order, delivery_status: 'ACCEPTED' } : order
            ));
        };

        // Handle order completed/delivered (via partner room)
        const handleOrderCompleted = ({ orderId, riderId, earnings }) => {
            setOrders(prev => prev.map(order =>
                order._id === orderId ? { ...order, delivery_status: 'DELIVERED' } : order
            ));
        };

        // Handle new order assigned to this partner
        const handleOrderAssignedTo3PL = ({ orderId, order, message }) => {
            // Add the new order to the list if not already present
            setOrders(prev => {
                const exists = prev.some(o => o._id === orderId);
                if (exists) return prev;
                return [order, ...prev];
            });
            playSound('assign');
            toast.success(message || 'New order assigned to your team!');
        };

        // Handle order recalled by vendor
        const handleOrderRecalledFrom3PL = ({ orderId, message }) => {
            // Remove the recalled order from the list
            setOrders(prev => prev.filter(order => order._id !== orderId));
            playSound('recall');
            toast.warning(message || 'An order was recalled by the vendor');
        };

        // Handle rider status changes (via partner room)
        const handleRiderStatusChange = ({ riderId, status, isAvailable }) => {
            setRiders(prevRiders =>
                prevRiders.map(rider =>
                    rider._id === riderId
                        ? { ...rider, onlineStatus: status, availability: { ...rider.availability, isAvailable } }
                        : rider
                )
            );
        };

        // Handle global rider status updates
        const handleGlobalRiderStatusUpdate = ({ riderId, status }) => {
            setRiders(prevRiders =>
                prevRiders.map(rider =>
                    rider._id === riderId
                        ? { ...rider, onlineStatus: status, availability: { ...rider.availability, isAvailable: status === 'Online' } }
                        : rider
                )
            );
        };

        // Handle real-time rider location updates
        const handleRiderLocationUpdated = ({ riderId, location, timestamp }) => {
            setRiders(prevRiders =>
                prevRiders.map(rider =>
                    rider._id === riderId
                        ? {
                            ...rider,
                            currentLocation: {
                                ...rider.currentLocation,
                                type: 'Point',
                                coordinates: [location.lng, location.lat],
                                heading: location.heading,
                                speed: location.speed,
                                lastUpdated: timestamp || new Date().toISOString()
                            }
                        }
                        : rider
                )
            );
        };

        socket.on('order:status_changed', handleStatusChange);
        socket.on('order:accepted', handleOrderAccepted);
        socket.on('order:completed', handleOrderCompleted);
        socket.on('order:assignedTo3PL', handleOrderAssignedTo3PL);

        // ─── Auto-Assignment Engine Socket Listeners ─────────────────
        const handleAutoAssignPending = (data) => {
            setAutoAssignActivity(`⏳ Auto-assigning order #${data.orderId?.slice(-6)} in ${data.cooldownSeconds}s...`);
        };
        const handleAutoAssignAssigned = (data) => {
            setAutoAssignActivity(`✅ Auto-assigned to ${data.rider?.name} (${data.rider?.distance} km) — attempt ${data.attempt}/${data.maxRetries}`);
            toast.info(`Auto-assigned order to ${data.rider?.name} (${data.rider?.distance} km away)`);
            fetchOrders(); // Refresh order list
        };
        const handleAutoAssignCascading = (data) => {
            setAutoAssignActivity(`🔄 Rider rejected/timed out — trying next rider (attempt ${data.attempt + 1}/${data.maxRetries})...`);
        };
        const handleAutoAssignResult = (data) => {
            if (data.status === 'exhausted' || data.status === 'no_riders') {
                setAutoAssignActivity(`⚠️ ${data.message} — manual assignment needed`);
                toast.warn(data.message);
            } else if (data.status === 'error' || data.status === 'failed') {
                setAutoAssignActivity(`❌ ${data.message}`);
            }
            // Clear activity after 15 seconds
            setTimeout(() => setAutoAssignActivity(null), 15000);
        };

        socket.on('autoAssign:pending', handleAutoAssignPending);
        socket.on('autoAssign:assigned', handleAutoAssignAssigned);
        socket.on('autoAssign:cascading', handleAutoAssignCascading);
        socket.on('autoAssign:result', handleAutoAssignResult);
        socket.on('order:recalledFrom3PL', handleOrderRecalledFrom3PL);
        socket.on('rider:statusChanged', handleRiderStatusChange);
        socket.on('riderStatusUpdate', handleGlobalRiderStatusUpdate);
        socket.on('rider:locationUpdated', handleRiderLocationUpdated);

        // Join partner room for targeted updates
        if (partnerId) {
            socket.emit('join3PLPartnerRoom', partnerId);
        }

        return () => {
            socket.off('order:status_changed', handleStatusChange);
            socket.off('order:accepted', handleOrderAccepted);
            socket.off('order:completed', handleOrderCompleted);
            socket.off('order:assignedTo3PL', handleOrderAssignedTo3PL);
            socket.off('autoAssign:pending', handleAutoAssignPending);
            socket.off('autoAssign:assigned', handleAutoAssignAssigned);
            socket.off('autoAssign:cascading', handleAutoAssignCascading);
            socket.off('autoAssign:result', handleAutoAssignResult);
            socket.off('order:recalledFrom3PL', handleOrderRecalledFrom3PL);
            socket.off('rider:statusChanged', handleRiderStatusChange);
            socket.off('riderStatusUpdate', handleGlobalRiderStatusUpdate);
            socket.off('rider:locationUpdated', handleRiderLocationUpdated);
            if (partnerId) {
                socket.emit('leave3PLPartnerRoom', partnerId);
            }
        };
    }, [partnerId]);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            // Fetch orders assigned to partner's riders
            const response = await axios.get(`${API_URL}/api/3pl-partners/${partnerId}/orders`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.data.success) {
                setOrders(response.data.data?.orders || []);
            }
        } catch (err) {
            console.error('Error fetching orders:', err);
            // Fallback - try regular orders endpoint with partner filter
            try {
                const ridersRes = await axios.get(`${API_URL}/api/3pl-partners/${partnerId}/riders`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const partnerRiders = ridersRes.data.data?.riders || [];
                const riderIds = partnerRiders.map(r => r._id);

                // For now, set empty if can't fetch
                setOrders([]);
            } catch (e) {
                setOrders([]);
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchRiders = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/3pl-partners/${partnerId}/riders`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.data.success) {
                setRiders(response.data.data?.riders || []);
            }
        } catch (err) {
            console.error('Error fetching riders:', err);
        }
    };

    // ─── Auto-Assignment Engine ──────────────────────────────────────────
    const fetchAutoAssignStatus = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/3pl-partners/${partnerId}/auto-assign`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.data.success) {
                const s = response.data.autoAssign;
                setAutoAssignEnabled(s?.enabled || false);
                setAutoAssignSettings(prev => ({
                    ...prev,
                    strategy: s?.strategy || prev.strategy,
                    maxRadiusKm: s?.maxRadiusKm ?? prev.maxRadiusKm,
                    maxRetries: s?.maxRetries ?? prev.maxRetries,
                    cooldownSeconds: s?.cooldownSeconds ?? prev.cooldownSeconds,
                    autoRejectTimeoutSeconds: s?.autoRejectTimeoutSeconds ?? prev.autoRejectTimeoutSeconds,
                }));
            }
        } catch (err) {
            console.error('Error fetching auto-assign status:', err);
        }
    };

    const handleSaveAutoAssignSettings = async () => {
        setSettingsSaving(true);
        try {
            const response = await axios.put(
                `${API_URL}/api/3pl-partners/${partnerId}/auto-assign/settings`,
                autoAssignSettings,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data.success) {
                toast.success('Auto-assign settings saved');
                setShowAutoAssignSettings(false);
            }
        } catch (err) {
            toast.error('Failed to save auto-assign settings');
            console.error('Auto-assign settings save error:', err);
        } finally {
            setSettingsSaving(false);
        }
    };

    const handleAutoAssignToggle = async () => {
        const newState = !autoAssignEnabled;
        setAutoAssignLoading(true);
        try {
            const response = await axios.post(
                `${API_URL}/api/3pl-partners/${partnerId}/auto-assign/toggle`,
                { enabled: newState },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data.success) {
                setAutoAssignEnabled(response.data.autoAssign?.enabled);
                toast.success(`Auto-assignment ${newState ? 'enabled' : 'disabled'}`);
                if (newState) {
                    setAutoAssignActivity('⚡ Auto-assignment engine activated \u2014 incoming orders will be assigned automatically');
                    setTimeout(() => setAutoAssignActivity(null), 10000);
                } else {
                    setAutoAssignActivity(null);
                }
            }
        } catch (err) {
            toast.error('Failed to toggle auto-assignment');
            console.error('Auto-assign toggle error:', err);
        } finally {
            setAutoAssignLoading(false);
        }
    };

    // Fetch auto-assign status on mount
    useEffect(() => {
        if (partnerId && token) {
            fetchAutoAssignStatus();
        }
    }, [partnerId, token]);

    // Drives every offer countdown on the page from a single interval, cleared
    // on unmount so a navigated-away screen stops re-rendering in the dark.
    useEffect(() => {
        const id = setInterval(() => setOfferClockTick(Date.now()), 30000);
        return () => clearInterval(id);
    }, []);

    const getOrdersByStatus = () => {
        // Include PENDING_3PL as pending - these are orders assigned to the 3PL partner waiting for rider assignment
        const pending = orders.filter(o => ['PROCESSING', 'PENDING_3PL', 'ASSIGNED'].includes(o.delivery_status || o.assignmentDetails?.status || o.deliveryStatus));
        const inTransit = orders.filter(o => ['ACCEPTED', 'DISPATCHED', 'IN_TRANSIT', 'PICKED_UP'].includes(o.delivery_status || o.assignmentDetails?.status || o.deliveryStatus));
        const completed = orders.filter(o => (o.delivery_status === 'DELIVERED') || o.assignmentDetails?.status === 'DELIVERED' || o.deliveryStatus === 'DELIVERED');
        return { pending, inTransit, completed, all: orders };
    };

    const filteredOrders = () => {
        const { pending, inTransit, completed, all } = getOrdersByStatus();
        let result;
        switch (activeTab) {
            case 'pending': result = pending; break;
            case 'inTransit': result = inTransit; break;
            case 'completed': result = completed; break;
            default: result = all;
        }

        // ── Date Range Filter ──
        // IMPORTANT: For the 3PL partner dashboard, we filter by `assignmentDetails.assignedAt`
        // (when the order was assigned to THIS partner), NOT by `createdAt` (when the customer
        // originally placed the order). An order placed yesterday but assigned to 3PL today
        // should appear under "Today". If assignedAt is unavailable, we fall back to createdAt.
        const getOrderDate = (o) => {
            const assignedAt = o.assignmentDetails?.assignedAt;
            return new Date(assignedAt || o.createdAt);
        };

        if (dateRange !== 'all') {
            const now = new Date();
            // Build local-time day boundaries to avoid UTC offset issues
            const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const yesterdayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);

            if (dateRange === 'today') {
                result = result.filter(o => getOrderDate(o) >= todayStart);
            } else if (dateRange === 'yesterday') {
                result = result.filter(o => {
                    const d = getOrderDate(o);
                    return d >= yesterdayStart && d < todayStart;
                });
            } else if (dateRange === '7days') {
                const start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                result = result.filter(o => getOrderDate(o) >= start);
            } else if (dateRange === '30days') {
                const start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                result = result.filter(o => getOrderDate(o) >= start);
            }
        }

        // ── Status Filter ──
        if (statusFilter !== 'all') {
            // Map grouped filter values to arrays matching tab definitions
            const statusGroups = {
                'GROUP_PENDING': ['PROCESSING', 'PENDING_3PL', 'ASSIGNED'],
                'GROUP_IN_TRANSIT': ['ACCEPTED', 'DISPATCHED', 'IN_TRANSIT', 'PICKED_UP'],
                'GROUP_COMPLETED': ['DELIVERED'],
            };
            const matchStatuses = statusGroups[statusFilter] || [statusFilter];
            result = result.filter(o => {
                const s = o.delivery_status || o.assignmentDetails?.status || o.deliveryStatus || '';
                return matchStatuses.includes(s);
            });
        }

        // ── Sort ── (sort by assignedAt for 3PL context, fallback to createdAt)
        result = [...result].sort((a, b) => {
            const da = new Date(a.assignmentDetails?.assignedAt || a.createdAt).getTime();
            const db = new Date(b.assignmentDetails?.assignedAt || b.createdAt).getTime();
            return sortOrder === 'newest' ? db - da : da - db;
        });

        return result;
    };

    const handleViewDetails = (order) => {
        setSelectedOrder(order);
        setShowDetailsModal(true);
    };

    const handleAssignOrder = (order) => {
        setSelectedOrder(order);
        setSelectedRider('');
        setRiderSearch('');
        setForceAssign(false);
        setShowAssignModal(true);
    };

    const handleAssignSubmit = async () => {
        if (!selectedRider) {
            toast.error('Please select a rider');
            return;
        }

        setAssigning(true);
        try {
            await axios.post(`${API_URL}/weborders/${selectedOrder._id}/assign`, {
                riderId: selectedRider,
                ...(forceAssign && { forceAssign: true }),
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success('Order assigned successfully');
            setShowAssignModal(false);
            fetchOrders();
        } catch (err) {
            const errorMessage = err.response?.data?.error?.message || err.response?.data?.message || 'Failed to assign order';
            toast.error(errorMessage);
        } finally {
            setAssigning(false);
        }
    };

    const handleUnassignOrder = async (order) => {
        setUnassignBusyId(order._id);
        try {
            const { data } = await axios.post(
                `${API_URL}/weborders/${order._id}/reassign`,
                {
                    unassign: true,
                    reason: (unassignReasonRef.current || '').trim() || 'Returned to dispatch queue by manager',
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(data?.data?.message || 'Rider unassigned; order returned to the queue');
            await Promise.all([fetchOrders(), fetchRiders()]);
        } catch (err) {
            const message = err.response?.data?.error?.message || err.response?.data?.message || 'Failed to unassign rider';
            toast.error(message);
        } finally {
            setUnassignBusyId(null);
        }
    };

    const askUnassignOrder = (order) => {
        unassignReasonRef.current = '';
        const riderName = order.assignmentDetails?.assignedRiderId?.firstName || 'this rider';
        confirmDialog.ask({
            tone: 'warning',
            title: `Unassign ${riderName}?`,
            body: (
                <>
                    <p style={{ marginBottom: 12 }}>
                        The order will return to your dispatch queue. The rider will be notified and can no longer accept or fulfil it.
                    </p>
                    <Form.Label style={{ fontSize: 12, fontWeight: 600, color: colors.muted }}>
                        Reason (recommended)
                    </Form.Label>
                    <Form.Control
                        type="text"
                        maxLength={140}
                        placeholder="e.g. rider unavailable or wrong assignment"
                        onChange={(e) => { unassignReasonRef.current = e.target.value; }}
                    />
                </>
            ),
            confirmLabel: 'Unassign rider',
            busyLabel: 'Unassigning...',
            onConfirm: () => handleUnassignOrder(order),
        });
    };

    const handleAcceptOffer = async (order) => {
        setOfferBusyId(order._id);
        try {
            const { data } = await apiAcceptPartnerOffer(partnerId, order._id);
            // Shown verbatim: the server already names the guaranteed minutes and
            // the area the promise came from, or says no guarantee applies. Rebuilt
            // here from the data block it would drift from what the server decided.
            toast.success(data?.message || 'Order accepted');
            fetchOrders();
        } catch (err) {
            const errorMessage = err.response?.data?.error?.message || err.response?.data?.message || 'Failed to accept this order';
            toast.error(errorMessage);
        } finally {
            setOfferBusyId(null);
        }
    };

    const handleDeclineOffer = async (order) => {
        setOfferBusyId(order._id);
        try {
            // A blank reason is a valid answer. It is context for the vendor, not
            // a condition of declining, so it never blocks the request.
            const { data } = await apiDeclinePartnerOffer(partnerId, order._id, (declineReasonRef.current || '').trim());
            toast.success(data?.message || 'Order returned to the vendor');
            fetchOrders();
        } catch (err) {
            const errorMessage = err.response?.data?.error?.message || err.response?.data?.message || 'Failed to decline this order';
            toast.error(errorMessage);
        } finally {
            setOfferBusyId(null);
        }
    };

    // Declining is not undoable from this screen, and it costs the partner an
    // order, so it is confirmed in-app rather than fired on a single click.
    const askDeclineOffer = (order) => {
        declineReasonRef.current = '';
        confirmDialog.ask({
            tone: 'danger',
            title: `Decline order #${order._id?.slice(-6).toUpperCase()}?`,
            body: (
                <>
                    <p style={{ marginBottom: 12 }}>
                        This sends the order back to the vendor. It leaves your queue, none of
                        your riders can be assigned to it, and the vendor has to place it with
                        someone else.
                    </p>
                    <Form.Label style={{ fontSize: 12, fontWeight: 600, color: colors.muted }}>
                        Reason (optional)
                    </Form.Label>
                    <Form.Control
                        type="text"
                        maxLength={140}
                        defaultValue=""
                        placeholder="e.g. no rider free in that area"
                        onChange={(e) => { declineReasonRef.current = e.target.value; }}
                    />
                </>
            ),
            confirmLabel: 'Decline and return',
            busyLabel: 'Returning...',
            onConfirm: () => handleDeclineOffer(order),
        });
    };

    // How long is left to answer. Past zero this states what is about to happen
    // instead of counting into negative numbers, because the durable timeout job
    // is the authority on the return and it may not have run yet.
    const renderOfferCountdown = (order) => {
        const expiresAt = order.assignmentDetails?.partnerOfferExpiresAt;
        if (!expiresAt) return null;
        const msLeft = new Date(expiresAt).getTime() - offerClockTick;
        if (!Number.isFinite(msLeft)) return null;
        if (msLeft <= 0) {
            return <span style={pill('bad')}>Returning to vendor</span>;
        }
        // Under five minutes the offer is about to be taken away, so it reads in
        // the refusal colour rather than the warning one.
        const urgent = msLeft < 5 * 60 * 1000;
        return (
            <span style={pill(urgent ? 'bad' : 'warn')}>
                {Math.ceil(msLeft / 60000)} min left to answer
                <PartnerHelp topic="offerWindow" />
            </span>
        );
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('en-UG', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
        });
    };

    const formatCurrency = (amount) => {
        return `UGX ${(amount || 0).toLocaleString()}`;
    };

    // Calculate distance using the Haversine formula
    const calculateDistance = (lat1, lon1, lat2, lon2) => {
        const R = 6371; // Radius of the Earth in kilometers
        const dLat = ((lat2 - lat1) * Math.PI) / 180;
        const dLon = ((lon2 - lon1) * Math.PI) / 180;
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos((lat1 * Math.PI) / 180) *
            Math.cos((lat2 * Math.PI) / 180) *
            Math.sin(dLon / 2) *
            Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return (R * c).toFixed(2);
    };

    // Get rider's distance from a specific order's vendor (pickup point)
    const getRiderDistanceFromOrder = (rider, order) => {
        if (!rider?.currentLocation?.coordinates ||
            rider.currentLocation.coordinates[0] === 0 && rider.currentLocation.coordinates[1] === 0) {
            return null; // No valid location
        }
        const origin = getOriginCoords(order);
        if (!origin.lat || !origin.lng) return null;

        const [riderLng, riderLat] = rider.currentLocation.coordinates; // GeoJSON: [lng, lat]
        return calculateDistance(riderLat, riderLng, origin.lat, origin.lng);
    };

    // Get rider distance from the closest pending order (for the rider status bar)
    const getRiderProximityBadge = (rider) => {
        const pendingOrders = orders.filter(o =>
            ['PROCESSING', 'PENDING_3PL'].includes(o.delivery_status || o.assignmentDetails?.status)
        );
        if (pendingOrders.length === 0) return null;

        // Find the closest pending order
        let minDistance = Infinity;
        for (const order of pendingOrders) {
            const dist = getRiderDistanceFromOrder(rider, order);
            if (dist !== null && parseFloat(dist) < minDistance) {
                minDistance = parseFloat(dist);
            }
        }

        if (minDistance === Infinity) return null;

        let color, bgColor;
        if (minDistance <= 2) {
            color = '#16a34a'; bgColor = 'rgba(34, 197, 94, 0.15)'; // Green — very close
        } else if (minDistance <= 5) {
            color = '#2563eb'; bgColor = 'rgba(37, 99, 235, 0.1)'; // Blue — nearby
        } else if (minDistance <= 10) {
            color = '#d97706'; bgColor = 'rgba(245, 158, 11, 0.1)'; // Amber — moderate
        } else {
            color = '#dc2626'; bgColor = 'rgba(220, 38, 38, 0.1)'; // Red — far
        }

        return (
            <span style={{
                fontSize: '10px',
                fontWeight: 600,
                color,
                backgroundColor: bgColor,
                padding: '2px 6px',
                borderRadius: '10px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '2px',
                lineHeight: 1
            }}>
                📍 {minDistance < 1 ? `${Math.round(minDistance * 1000)}m` : `${minDistance.toFixed(1)}km`}
            </span>
        );
    };

    // Get origin coordinates from order
    const getOriginCoords = (order) => {
        const item = order?.orderItems?.[0];
        // Check order.restaurantData first (populated by backend location resolution)
        // Then fallback to populated fields from orderItems
        const lat = order?.restaurantData?.latitude ||
            item?.outletId?.latitude ||
            item?.parentRestaurantId?.latitude ||
            item?.restaurantData?.latitude ||
            item?.vendorId?.latitude ||
            order?.pickupAddress?.latLng?.lat;
        const lng = order?.restaurantData?.longitude ||
            item?.outletId?.longitude ||
            item?.parentRestaurantId?.longitude ||
            item?.restaurantData?.longitude ||
            item?.vendorId?.longitude ||
            order?.pickupAddress?.latLng?.lng;
        const name = order?.restaurantData?.businessName ||
            item?.outletId?.name ||
            item?.parentRestaurantId?.name ||
            item?.restaurantData?.name ||
            item?.vendorId?.businessName ||
            order?.pickupAddress?.address ||
            'N/A';
        const address = order?.restaurantData?.address ||
            item?.outletId?.address ||
            item?.parentRestaurantId?.address ||
            item?.restaurantData?.address ||
            item?.vendorId?.businessAddress ||
            order?.pickupAddress?.address ||
            'N/A';
        const zoneRadius = order?.restaurantData?.deliveryZoneRadius ||
            item?.outletId?.deliveryZoneRadius ||
            item?.parentRestaurantId?.deliveryZoneRadius ||
            item?.restaurantData?.deliveryZoneRadius ||
            item?.vendorId?.deliveryZoneRadius ||
            5;
        return { lat, lng, name, address, zoneRadius };
    };

    // Time tracking badge - shows elapsed time with color-coded urgency
    const getTimeBadge = (assignedAt, status) => {
        // Don't show for completed orders
        if (status === 'DELIVERED') return null;

        if (!assignedAt) {
            return <Badge bg="secondary" style={{ fontSize: '10px' }}>Not assigned</Badge>;
        }

        const now = new Date();
        const assigned = new Date(assignedAt);
        const diffMs = now - assigned;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMins / 60);

        let badge = { bg: 'success', text: '' };

        if (diffMins < 30) {
            badge = { bg: 'success', text: `${diffMins}m ago` };  // Green - on track
        } else if (diffMins < 60) {
            badge = { bg: 'warning', text: `${diffMins}m ago` };  // Yellow - getting close
        } else if (diffHours < 2) {
            badge = { bg: 'orange', text: `${diffHours}h ${diffMins % 60}m ago` };  // Orange - delayed
        } else {
            badge = { bg: 'danger', text: `${diffHours}h ${diffMins % 60}m ago` };  // Red - critical delay
        }

        return (
            <Badge
                bg={badge.bg === 'orange' ? undefined : badge.bg}
                style={{
                    fontSize: '10px',
                    backgroundColor: badge.bg === 'orange' ? '#fd7e14' : undefined
                }}
            >
                ⏱ {badge.text}
            </Badge>
        );
    };

    const { pending, inTransit, completed } = getOrdersByStatus();

    if (loading) {
        return (
            <div className="content d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <Spinner animation="grow" style={{ color: "rgba(179, 167, 10, 0.84)" }} />
            </div>
        );
    }

    return (
        <div className="content" style={{ marginTop: '-50px' }}>
            {/* ─── Braun-Inspired Keyframes ────────────────────────────────── */}
            <style>{`
                @keyframes slideDown {
                    from { opacity: 0; transform: translateY(-6px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50%      { opacity: 0.35; }
                }
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to   { transform: rotate(360deg); }
                }
                @keyframes liveDot {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(16,185,129,0.45); }
                    50%      { box-shadow: 0 0 0 4px rgba(16,185,129,0); }
                }
                /* Sticky control bar */
                .braun-control-bar {
                    position: sticky;
                    top: 0;
                    z-index: 100;
                    margin: 0 -15px 0;
                    padding: 0;
                }
                .braun-control-bar .braun-inner {
                    background: #fff;
                    border-bottom: 1px solid #e5e7eb;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
                    transition: box-shadow 0.2s ease;
                }
                .braun-control-bar.is-stuck .braun-inner {
                    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
                }
                /* Control tile — Braun: geometric, tactile, monochromatic */
                .braun-tile {
                    position: relative;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 18px;
                    border: 1px solid #e5e7eb;
                    border-radius: 6px;
                    background: #fff;
                    color: #6b7280;
                    font-size: 13px;
                    font-weight: 500;
                    letter-spacing: 0.02em;
                    cursor: pointer;
                    user-select: none;
                    transition: all 0.2s ease;
                    outline: none;
                    white-space: nowrap;
                    line-height: 1;
                }
                .braun-tile:hover {
                    background: #f9fafb;
                    border-color: #d1d5db;
                    color: #374151;
                }
                .braun-tile:active {
                    transform: scale(0.97);
                }
                /* Active state — vivid emerald with bold accent bar */
                .braun-tile.is-on {
                    border-color: #059669;
                    background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
                    color: #064e3b;
                    font-weight: 600;
                    box-shadow: 0 1px 4px rgba(5,150,105,0.15);
                }
                .braun-tile.is-on::before {
                    content: '';
                    position: absolute;
                    top: -1px;
                    left: 0;
                    right: 0;
                    height: 3px;
                    background: linear-gradient(90deg, #059669, #10b981);
                    border-radius: 0 0 3px 3px;
                }
                /* Warning state — vivid amber */
                .braun-tile.is-warning {
                    border-color: #d97706;
                    background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
                    color: #78350f;
                    font-weight: 600;
                    box-shadow: 0 1px 4px rgba(217,119,6,0.15);
                }
                .braun-tile.is-warning::before {
                    content: '';
                    position: absolute;
                    top: -1px;
                    left: 0;
                    right: 0;
                    height: 3px;
                    background: linear-gradient(90deg, #d97706, #f59e0b);
                    border-radius: 0 0 3px 3px;
                }
                .braun-tile .braun-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 28px;
                    height: 28px;
                    border-radius: 6px;
                    background: #f3f4f6;
                    flex-shrink: 0;
                    transition: all 0.2s;
                }
                .braun-tile.is-on .braun-icon {
                    background: rgba(5,150,105,0.18);
                }
                .braun-tile.is-warning .braun-icon {
                    background: rgba(217,119,6,0.18);
                }
                .braun-tile .braun-live {
                    width: 7px;
                    height: 7px;
                    border-radius: 50%;
                    background: #059669;
                    animation: liveDot 1.5s ease-in-out infinite;
                    flex-shrink: 0;
                }
                .braun-tile.is-active-settings {
                    border-color: #6366f1;
                    background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
                    color: #3730a3;
                    box-shadow: 0 1px 4px rgba(99,102,241,0.15);
                }
                .braun-tile.is-active-settings .braun-icon {
                    background: rgba(99,102,241,0.15);
                }
                /* Settings panel */
                .braun-settings-panel {
                    margin: 0 -15px 16px;
                    background: #fff;
                    border: 1px solid #e5e7eb;
                    border-top: none;
                    border-radius: 0 0 10px 10px;
                    box-shadow: 0 4px 16px rgba(0,0,0,0.06);
                    animation: slideDown 0.25s ease-out;
                    overflow: hidden;
                }
                .braun-settings-panel .braun-sp-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 14px 20px 10px;
                    border-bottom: 1px solid #f3f4f6;
                }
                .braun-settings-panel .braun-sp-header h5 {
                    margin: 0;
                    font-size: 14px;
                    font-weight: 700;
                    color: #1f2937;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                .braun-settings-panel .braun-sp-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
                    gap: 16px;
                    padding: 16px 20px;
                }
                .braun-settings-panel .braun-sp-field label {
                    display: block;
                    font-size: 11px;
                    font-weight: 600;
                    color: #6b7280;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    margin-bottom: 6px;
                }
                .braun-settings-panel .braun-sp-field select,
                .braun-settings-panel .braun-sp-field input {
                    width: 100%;
                    padding: 8px 12px;
                    border: 1px solid #d1d5db;
                    border-radius: 6px;
                    font-size: 13px;
                    color: #1f2937;
                    background: #fff;
                    transition: border-color 0.2s, box-shadow 0.2s;
                    outline: none;
                }
                .braun-settings-panel .braun-sp-field select:focus,
                .braun-settings-panel .braun-sp-field input:focus {
                    border-color: #6366f1;
                    box-shadow: 0 0 0 3px rgba(99,102,241,0.1);
                }
                .braun-settings-panel .braun-sp-field .braun-sp-hint {
                    font-size: 11px;
                    color: #9ca3af;
                    margin-top: 4px;
                }
                .braun-settings-panel .braun-sp-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 8px;
                    padding: 12px 20px;
                    border-top: 1px solid #f3f4f6;
                    background: #fafafa;
                }
                /* Activity strip */
                .braun-activity {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 6px 20px;
                    font-size: 12px;
                    font-weight: 500;
                    color: #374151;
                    background: #f9fafb;
                    border-top: 1px solid #f3f4f6;
                    animation: slideDown 0.25s ease-out;
                    line-height: 1.4;
                }
                .braun-activity .braun-act-dismiss {
                    margin-left: auto;
                    padding: 0 4px;
                    cursor: pointer;
                    color: #9ca3af;
                    font-size: 14px;
                    line-height: 1;
                }
                .braun-activity .braun-act-dismiss:hover {
                    color: #374151;
                }
                /* Filter bar */
                .braun-filter-bar {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 10px 0 14px;
                    flex-wrap: wrap;
                    border-bottom: 1px solid #f3f4f6;
                    margin-bottom: 14px;
                }
                .braun-filter-group {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    flex-wrap: wrap;
                }
                .braun-filter-chip {
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    padding: 5px 12px;
                    font-size: 12px;
                    font-weight: 500;
                    color: #6b7280;
                    background: #f9fafb;
                    border: 1px solid #e5e7eb;
                    border-radius: 16px;
                    cursor: pointer;
                    white-space: nowrap;
                    transition: all 0.18s ease;
                    outline: none;
                    line-height: 1.3;
                }
                .braun-filter-chip:hover {
                    background: #f3f4f6;
                    border-color: #d1d5db;
                    color: #374151;
                }
                .braun-filter-chip.active {
                    background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
                    border-color: #818cf8;
                    color: #4338ca;
                    font-weight: 600;
                    box-shadow: 0 1px 3px rgba(99,102,241,0.12);
                }
                .braun-filter-select {
                    padding: 5px 28px 5px 10px;
                    font-size: 12px;
                    font-weight: 500;
                    color: #374151;
                    background: #f9fafb;
                    border: 1px solid #e5e7eb;
                    border-radius: 16px;
                    cursor: pointer;
                    outline: none;
                    transition: all 0.18s ease;
                    appearance: none;
                    -webkit-appearance: none;
                    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='%236b7280'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E");
                    background-repeat: no-repeat;
                    background-position: right 10px center;
                }
                .braun-filter-select:hover {
                    border-color: #d1d5db;
                    background-color: #f3f4f6;
                }
                .braun-filter-select:focus {
                    border-color: #818cf8;
                    box-shadow: 0 0 0 2px rgba(99,102,241,0.12);
                }
                .braun-filter-count {
                    font-size: 12px;
                    font-weight: 600;
                    color: #6b7280;
                    padding: 4px 10px;
                    background: #f3f4f6;
                    border-radius: 10px;
                    white-space: nowrap;
                }
            `}</style>

            {/* ─── Sticky Control Bar ─────────────────────────────────────── */}
            <div className="braun-control-bar" id="partnerControlBar">
                <div className="braun-inner">
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px 20px',
                        gap: '16px',
                        flexWrap: 'wrap',
                    }}>
                        {/* Left: Page Title */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                            <h4 style={{
                                color: '#1f2937',
                                fontSize: '16px',
                                fontWeight: 700,
                                margin: 0,
                                letterSpacing: '-0.01em',
                                lineHeight: 1.3,
                            }}>
                                Order Management
                            </h4>
                            <span style={{
                                color: '#9ca3af',
                                fontSize: '12px',
                                fontWeight: 400,
                                letterSpacing: '0.01em',
                            }}>
                                {orders.length} order{orders.length !== 1 ? 's' : ''} · {riders.filter(r => r.onlineStatus === 'Online').length} rider{riders.filter(r => r.onlineStatus === 'Online').length !== 1 ? 's' : ''} online
                            </span>
                        </div>

                        {/* Right: Control Tiles */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                            {/* 1. Audio Alerts Tile */}
                            <OverlayTrigger
                                placement="bottom"
                                overlay={
                                    <Tooltip>
                                        {audioEnabled
                                            ? (audioReady
                                                ? 'Audio alerts active — you\'ll hear a sound on new orders'
                                                : 'Click anywhere on page to unlock browser audio')
                                            : 'Audio alerts off — enable to hear order notifications'}
                                    </Tooltip>
                                }
                            >
                                <button
                                    className={`braun-tile ${audioEnabled ? (audioReady ? 'is-on' : 'is-warning') : ''}`}
                                    onClick={toggleAudio}
                                    style={audioEnabled && !audioReady ? { animation: 'pulse 2s infinite' } : undefined}
                                >
                                    <span className="braun-icon">
                                        {audioEnabled
                                            ? <MdVolumeUp size={16} color={audioReady ? '#059669' : '#d97706'} />
                                            : <MdVolumeOff size={16} color="#9ca3af" />
                                        }
                                    </span>
                                    {audioEnabled
                                        ? (audioReady ? 'Alerts' : 'Activate')
                                        : 'Alerts'
                                    }
                                    {audioEnabled && audioReady && <span className="braun-live" />}
                                </button>
                            </OverlayTrigger>

                            {/* Vertical separator */}
                            <div style={{ width: 1, height: 24, background: '#e5e7eb', flexShrink: 0 }} />

                            {/* 2. Auto-Assign Tile */}
                            <OverlayTrigger
                                placement="bottom"
                                overlay={
                                    <Tooltip>
                                        {autoAssignEnabled
                                            ? 'Auto-assign ON — orders routed to nearest available rider'
                                            : 'Auto-assign OFF — orders require manual rider assignment'}
                                    </Tooltip>
                                }
                            >
                                <button
                                    className={`braun-tile ${autoAssignEnabled ? 'is-on' : ''}`}
                                    onClick={handleAutoAssignToggle}
                                    disabled={autoAssignLoading}
                                    style={autoAssignLoading ? { opacity: 0.6, pointerEvents: 'none' } : undefined}
                                >
                                    <span className="braun-icon">
                                        {autoAssignLoading ? (
                                            <Spinner animation="border" size="sm" style={{ width: 14, height: 14, borderWidth: 2, color: '#6b7280' }} />
                                        ) : autoAssignEnabled ? (
                                            <MdFlashOn size={16} color="#059669" />
                                        ) : (
                                            <MdFlashOff size={16} color="#9ca3af" />
                                        )}
                                    </span>
                                    Auto-Assign
                                    {autoAssignEnabled && <span className="braun-live" />}
                                </button>
                            </OverlayTrigger>

                            {/* Vertical separator */}
                            <div style={{ width: 1, height: 24, background: '#e5e7eb', flexShrink: 0 }} />

                            {/* 3. Refresh Tile */}
                            <OverlayTrigger
                                placement="bottom"
                                overlay={<Tooltip>Reload orders and rider data</Tooltip>}
                            >
                                <button
                                    className="braun-tile"
                                    onClick={() => { fetchOrders(); fetchRiders(); }}
                                >
                                    <span className="braun-icon">
                                        <MdAutorenew size={16} color="#6b7280" />
                                    </span>
                                    Refresh
                                </button>
                            </OverlayTrigger>

                            {/* Vertical separator */}
                            <div style={{ width: 1, height: 24, background: '#e5e7eb', flexShrink: 0 }} />

                            {/* 4. Settings Tile */}
                            <OverlayTrigger
                                placement="bottom"
                                overlay={<Tooltip>Configure auto-assignment strategy and parameters</Tooltip>}
                            >
                                <button
                                    className={`braun-tile ${showAutoAssignSettings ? 'is-active-settings' : ''}`}
                                    onClick={() => setShowAutoAssignSettings(prev => !prev)}
                                >
                                    <span className="braun-icon">
                                        <MdTune size={16} color={showAutoAssignSettings ? '#6366f1' : '#6b7280'} />
                                    </span>
                                    Settings
                                </button>
                            </OverlayTrigger>
                        </div>
                    </div>

                    {/* Activity strip — integrated into sticky bar */}
                    {autoAssignActivity && (
                        <div className="braun-activity">
                            <MdAutorenew size={14} style={{
                                color: autoAssignActivity.includes('✅') ? '#10b981'
                                    : autoAssignActivity.includes('⚠️') || autoAssignActivity.includes('❌') ? '#f59e0b'
                                        : '#6b7280',
                                animation: autoAssignActivity.includes('...') ? 'spin 1s linear infinite' : 'none',
                                flexShrink: 0,
                            }} />
                            <span>
                                <strong style={{ color: '#1f2937', marginRight: 4 }}>Auto-Assign</strong>
                                {autoAssignActivity}
                            </span>
                            <span
                                className="braun-act-dismiss"
                                onClick={() => setAutoAssignActivity(null)}
                                title="Dismiss"
                            >
                                ×
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* ─── Auto-Assign Settings Panel ──────────────────────────────── */}
            {showAutoAssignSettings && (
                <div className="braun-settings-panel">
                    <div className="braun-sp-header">
                        <h5>
                            <MdTune size={18} color="#6366f1" />
                            Auto-Assignment Configuration
                        </h5>
                        <button
                            className="braun-tile"
                            onClick={() => setShowAutoAssignSettings(false)}
                            style={{ padding: '6px 10px', fontSize: '12px' }}
                        >
                            <MdClose size={14} /> Close
                        </button>
                    </div>
                    <div className="braun-sp-grid">
                        <div className="braun-sp-field">
                            <label>Assignment Strategy</label>
                            <AppSelect
                                value={autoAssignSettings.strategy}
                                onChange={e => setAutoAssignSettings(prev => ({ ...prev, strategy: e.target.value }))}
                            >
                                <option value="proximity">📍 Proximity (nearest rider)</option>
                                <option value="performance">⭐ Performance (best-rated rider)</option>
                                <option value="round_robin">🔄 Round Robin (fair rotation)</option>
                            </AppSelect>
                            <div className="braun-sp-hint">How riders are selected for new orders</div>
                        </div>
                        <div className="braun-sp-field">
                            <label>Cooldown (seconds)</label>
                            <input
                                type="number"
                                min="0"
                                max="300"
                                value={autoAssignSettings.cooldownSeconds}
                                onChange={e => setAutoAssignSettings(prev => ({ ...prev, cooldownSeconds: Math.max(0, parseInt(e.target.value) || 0) }))}
                            />
                            <div className="braun-sp-hint">Delay before auto-assigning (0 = instant)</div>
                        </div>
                        <div className="braun-sp-field">
                            <label>Max Radius (km)</label>
                            <input
                                type="number"
                                min="1"
                                max="100"
                                value={autoAssignSettings.maxRadiusKm}
                                onChange={e => setAutoAssignSettings(prev => ({ ...prev, maxRadiusKm: Math.max(1, parseInt(e.target.value) || 1) }))}
                            />
                            <div className="braun-sp-hint">Maximum distance to search for riders</div>
                        </div>
                        <div className="braun-sp-field">
                            <label>Max Retries</label>
                            <input
                                type="number"
                                min="1"
                                max="20"
                                value={autoAssignSettings.maxRetries}
                                onChange={e => setAutoAssignSettings(prev => ({ ...prev, maxRetries: Math.max(1, parseInt(e.target.value) || 1) }))}
                            />
                            <div className="braun-sp-hint">Riders to try before giving up</div>
                        </div>
                        <div className="braun-sp-field">
                            <label>Rider Accept Timeout (seconds)</label>
                            <input
                                type="number"
                                min="10"
                                max="300"
                                value={autoAssignSettings.autoRejectTimeoutSeconds}
                                onChange={e => setAutoAssignSettings(prev => ({ ...prev, autoRejectTimeoutSeconds: Math.max(10, parseInt(e.target.value) || 60) }))}
                            />
                            <div className="braun-sp-hint">Time a rider has to accept/reject</div>
                        </div>
                    </div>
                    <div className="braun-sp-actions">
                        <button
                            className="braun-tile"
                            onClick={() => setShowAutoAssignSettings(false)}
                            style={{ padding: '8px 16px' }}
                        >
                            Cancel
                        </button>
                        <button
                            className="braun-tile is-on"
                            onClick={handleSaveAutoAssignSettings}
                            disabled={settingsSaving}
                            style={{ padding: '8px 18px' }}
                        >
                            <span className="braun-icon" style={{ width: 22, height: 22 }}>
                                {settingsSaving
                                    ? <Spinner animation="border" size="sm" style={{ width: 12, height: 12, borderWidth: 2 }} />
                                    : <MdSave size={14} color="#059669" />
                                }
                            </span>
                            {settingsSaving ? 'Saving...' : 'Save Settings'}
                        </button>
                    </div>
                </div>
            )}

            {error && <Alert variant="danger">{error}</Alert>}

            {/* Riders Status Container */}
            {riders.length > 0 && (
                <div style={{
                    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                    borderRadius: '12px',
                    padding: '16px',
                    marginBottom: '20px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                }}>
                    <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-2">
                            <MdTwoWheeler size={20} style={{ color: colors.blue }} />
                            <span style={{ fontWeight: 600, color: colors.body, fontSize: '14px' }}>
                                Riders ({riders.filter(r => r.onlineStatus === 'Online').length} online / {riders.length} total)
                            </span>
                        </div>
                        {/* Filter Buttons */}
                        <div className="d-flex gap-1">
                            <Button
                                size="sm"
                                variant={riderFilter === 'all' ? 'primary' : 'outline-secondary'}
                                onClick={() => setRiderFilter('all')}
                                style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '20px' }}
                            >
                                All ({riders.length})
                            </Button>
                            <Button
                                size="sm"
                                variant={riderFilter === 'online' ? 'success' : 'outline-secondary'}
                                onClick={() => setRiderFilter('online')}
                                style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '20px' }}
                            >
                                🟢 Online ({riders.filter(r => r.onlineStatus === 'Online').length})
                            </Button>
                            <Button
                                size="sm"
                                variant={riderFilter === 'offline' ? 'secondary' : 'outline-secondary'}
                                onClick={() => setRiderFilter('offline')}
                                style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '20px' }}
                            >
                                ⚫ Offline ({riders.filter(r => r.onlineStatus !== 'Online').length})
                            </Button>
                        </div>
                    </div>
                    <div style={{
                        display: 'flex',
                        gap: '12px',
                        overflowX: 'auto',
                        paddingBottom: '8px',
                        scrollbarWidth: 'thin'
                    }}>
                        {/* Filter and sort riders: online first */}
                        {(() => {
                            let filteredRiders = [...riders];
                            if (riderFilter === 'online') {
                                filteredRiders = filteredRiders.filter(r => r.onlineStatus === 'Online');
                            } else if (riderFilter === 'offline') {
                                filteredRiders = filteredRiders.filter(r => r.onlineStatus !== 'Online');
                            }
                            // Sort: online first
                            filteredRiders.sort((a, b) => (b.onlineStatus === 'Online' ? 1 : 0) - (a.onlineStatus === 'Online' ? 1 : 0));

                            if (filteredRiders.length === 0) {
                                return (
                                    <div style={{ color: colors.muted, fontSize: '13px', padding: '10px' }}>
                                        No {riderFilter === 'online' ? 'online' : 'offline'} riders found
                                    </div>
                                );
                            }

                            return filteredRiders.map((rider) => {
                                const isOnline = rider.onlineStatus === 'Online';
                                return (
                                    <div
                                        key={rider._id}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            padding: '10px 14px',
                                            backgroundColor: isOnline ? 'rgba(34, 197, 94, 0.1)' : '#fff',
                                            borderRadius: '10px',
                                            border: `2px solid ${isOnline ? '#22c55e' : '#e5e7eb'}`,
                                            minWidth: '160px',
                                            cursor: 'default',
                                            transition: 'all 0.2s ease',
                                            boxShadow: isOnline ? '0 2px 8px rgba(34, 197, 94, 0.2)' : '0 1px 3px rgba(0,0,0,0.05)'
                                        }}
                                    >
                                        {/* Avatar with status indicator */}
                                        <div style={{ position: 'relative' }}>
                                            <div style={{
                                                width: '36px',
                                                height: '36px',
                                                borderRadius: '50%',
                                                backgroundColor: isOnline ? '#dcfce7' : colors.lineSoft,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: 600,
                                                fontSize: '14px',
                                                color: isOnline ? '#16a34a' : colors.muted
                                            }}>
                                                {rider.firstName?.charAt(0)?.toUpperCase() || 'R'}
                                            </div>
                                            {/* Online indicator dot */}
                                            <span style={{
                                                position: 'absolute',
                                                bottom: 0,
                                                right: 0,
                                                width: '12px',
                                                height: '12px',
                                                borderRadius: '50%',
                                                backgroundColor: isOnline ? '#22c55e' : '#9ca3af',
                                                border: '2px solid #fff',
                                                animation: isOnline ? 'pulse 2s infinite' : 'none'
                                            }} />
                                        </div>
                                        {/* Rider info */}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{
                                                fontWeight: 600,
                                                fontSize: '13px',
                                                color: colors.body,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}>
                                                {rider.firstName || 'Unknown'}
                                            </div>
                                            <div style={{
                                                fontSize: '11px',
                                                color: isOnline ? '#16a34a' : colors.muted,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px'
                                            }}>
                                                {isOnline ? '● Available' : '○ Offline'}
                                                {isOnline && getRiderProximityBadge(rider)}
                                            </div>
                                        </div>
                                    </div>
                                );
                            });
                        })()}
                    </div>
                </div>
            )}

            {/* Stats Cards. Each one is the tab it counts: clicking Pending
                opens the Pending list, and so on. Same numbers as the tabs
                below, so the two can never disagree. */}
            <style>{`
                .po-stat { cursor: pointer; transition: transform 0.15s ease, box-shadow 0.15s ease; }
                .po-stat:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(15,23,42,0.10); }
                .po-stat:focus-visible { outline: 3px solid #93c5fd; outline-offset: 2px; }
                .po-stat.is-active { box-shadow: 0 0 0 2px var(--stat-color) inset, 0 8px 20px rgba(15,23,42,0.08); }
                .po-stat .po-stat-hint { font-size: 11px; color: #94a3b8; margin-top: 2px; }
            `}</style>
            <Row className="mb-4">
                {[
                    { tab: 'pending', label: 'Pending', count: pending.length, color: colors.amber, icon: 'nc-time-alarm', hint: 'Waiting for a rider' },
                    { tab: 'inTransit', label: 'In Transit', count: inTransit.length, color: colors.blue, icon: 'nc-delivery-fast', hint: 'On the road now' },
                    { tab: 'completed', label: 'Completed', count: completed.length, color: colors.green, icon: 'nc-check-2', hint: 'Delivered' },
                    { tab: 'all', label: 'Total Orders', count: orders.length, color: colors.teal, icon: 'nc-chart-bar-32', hint: 'Everything' },
                ].map((stat) => (
                    <Col md="3" sm="6" key={stat.tab}>
                        <Card
                            className={`card-stats po-stat${activeTab === stat.tab ? ' is-active' : ''}`}
                            style={{ borderLeft: `4px solid ${stat.color}`, '--stat-color': stat.color }}
                            role="button"
                            tabIndex={0}
                            aria-pressed={activeTab === stat.tab}
                            title={`Show ${stat.label.toLowerCase()} orders`}
                            onClick={() => showOrdersTab(stat.tab)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); showOrdersTab(stat.tab); } }}
                        >
                            <CardBody>
                                <Row>
                                    <Col xs="5">
                                        <div className="icon-big text-center" style={{ color: stat.color }}>
                                            <i className={`nc-icon ${stat.icon}`} />
                                        </div>
                                    </Col>
                                    <Col xs="7">
                                        <div className="numbers">
                                            <p className="card-category">{stat.label}</p>
                                            <CardTitle tag="h4">{stat.count}</CardTitle>
                                            <div className="po-stat-hint">{stat.hint}</div>
                                        </div>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    </Col>
                ))}
            </Row>

            {/* Orders Tabs */}
            <Card ref={ordersCardRef}>
                <CardBody>
                    <Tabs
                        activeKey={activeTab}
                        onSelect={(k) => setActiveTab(k)}
                        className="mb-3"
                    >
                        <Tab eventKey="all" title={`All (${orders.length})`} />
                        <Tab eventKey="pending" title={`Pending (${pending.length})`} />
                        <Tab eventKey="inTransit" title={`In Transit (${inTransit.length})`} />
                        <Tab eventKey="completed" title={`Completed (${completed.length})`} />
                    </Tabs>

                    {/* ─── Filter Bar ─────────────────────────────────── */}
                    <div className="braun-filter-bar">
                        {/* Date Range Chips */}
                        <div className="braun-filter-group">
                            <MdCalendarToday size={14} style={{ color: '#6b7280', flexShrink: 0 }} />
                            {[
                                { key: 'today', label: 'Today' },
                                { key: 'yesterday', label: 'Yesterday' },
                                { key: '7days', label: '7 Days' },
                                { key: '30days', label: '30 Days' },
                                { key: 'all', label: 'All Time' },
                            ].map(opt => (
                                <button
                                    key={opt.key}
                                    className={`braun-filter-chip ${dateRange === opt.key ? 'active' : ''}`}
                                    onClick={() => setDateRange(opt.key)}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>

                        {/* Separator */}
                        <div style={{ width: 1, height: 24, background: '#e5e7eb', flexShrink: 0 }} />

                        {/* Status Filter */}
                        <div className="braun-filter-group">
                            <MdFilterList size={14} style={{ color: '#6b7280', flexShrink: 0 }} />
                            <AppSelect
                                className="braun-filter-select"
                                value={statusFilter}
                                onChange={e => setStatusFilter(e.target.value)}
                            >
                                <option value="all">All Statuses</option>
                                <optgroup label="─ Groups (match tabs)">
                                    <option value="GROUP_PENDING">Pending (all)</option>
                                    <option value="GROUP_IN_TRANSIT">In Transit (all)</option>
                                    <option value="GROUP_COMPLETED">Completed</option>
                                </optgroup>
                                <optgroup label="─ Individual">
                                    <option value="PENDING_3PL">Pending 3PL</option>
                                    <option value="PROCESSING">Processing</option>
                                    <option value="ASSIGNED">Assigned</option>
                                    <option value="ACCEPTED">Accepted</option>
                                    <option value="DISPATCHED">Dispatched</option>
                                    <option value="PICKED_UP">Picked Up</option>
                                    <option value="IN_TRANSIT">In Transit</option>
                                    <option value="DELIVERED">Delivered</option>
                                </optgroup>
                            </AppSelect>
                        </div>

                        {/* Separator */}
                        <div style={{ width: 1, height: 24, background: '#e5e7eb', flexShrink: 0 }} />

                        {/* Sort */}
                        <button
                            className="braun-filter-chip active"
                            onClick={() => setSortOrder(prev => prev === 'newest' ? 'oldest' : 'newest')}
                            style={{ gap: '4px' }}
                        >
                            <MdSort size={14} />
                            {sortOrder === 'newest' ? 'Newest First' : 'Oldest First'}
                        </button>

                        {/* Spacer */}
                        <div style={{ flex: 1 }} />

                        {/* Result Count */}
                        <span className="braun-filter-count">
                            {filteredOrders().length} order{filteredOrders().length !== 1 ? 's' : ''}
                        </span>

                        {/* Clear All (only if any filter is non-default) */}
                        {(dateRange !== 'today' || statusFilter !== 'all' || sortOrder !== 'newest') && (
                            <button
                                className="braun-filter-chip"
                                onClick={() => { setDateRange('today'); setStatusFilter('all'); setSortOrder('newest'); }}
                                style={{ color: '#ef4444', borderColor: '#fecaca', background: '#fef2f2' }}
                            >
                                <MdClear size={13} /> Reset
                            </button>
                        )}
                    </div>

                    {filteredOrders().length === 0 ? (
                        <div className="text-center py-5">
                            <i className="nc-icon nc-paper-2" style={{ fontSize: '48px', color: colors.line }} />
                            <p className="mt-3" style={{ color: colors.muted }}>
                                No orders found in this category.
                            </p>
                        </div>
                    ) : (
                        <div className="po-table-scroll">
                        <Table hover style={{ minWidth: '1180px', fontSize: '12px', marginBottom: 0 }}>
                            <style>{`
                                /* The dashboard theme turns .table-responsive into
                                   overflow:visible on desktop, so a wide table simply
                                   ran out of the card. This wrapper owns the scroll. */
                                .po-table-scroll { width: 100%; overflow-x: auto; overflow-y: hidden; -webkit-overflow-scrolling: touch; scrollbar-width: thin; padding-bottom: 4px; }
                                .po-actions-cell { white-space: nowrap; }
                                .po-actions-stack { display: flex; flex-direction: column; gap: 6px; align-items: flex-start; }
                                .po-actions-stack > div { display: flex; gap: 6px; align-items: center; flex-wrap: nowrap; }
                                .po-row { transition: background 0.15s ease; }
                                .po-row td { padding: 6px 8px !important; vertical-align: middle; }
                                .po-row:hover { background: #f8fafd !important; }
                                .po-row td:first-child { position: relative; }
                                .po-row td:first-child::before {
                                    content: '';
                                    position: absolute;
                                    left: 0; top: 0; bottom: 0;
                                    width: 3px;
                                    border-radius: 0 3px 3px 0;
                                    opacity: 0;
                                    transition: opacity 0.2s;
                                }
                                .po-row:hover td:first-child::before { opacity: 1; }
                                .po-action-btn {
                                    display: inline-flex;
                                    flex-direction: column;
                                    align-items: center;
                                    justify-content: center;
                                    gap: -1px;
                                    font-size: 12px;
                                    padding: 6px 12px;
                                    border-radius: 5px;
                                    font-weight: 500;
                                    white-space: nowrap;
                                    cursor: pointer;
                                    border: none;
                                    transition: all 0.15s ease;
                                }
                                .po-action-btn:active { transform: scale(0.96); }
                                .po-action-view {
                                    background: #eef2ff;
                                    color: #4338ca;
                                }
                                .po-action-view:hover { background: #e0e7ff; }
                                .po-action-assign {
                                    background: linear-gradient(135deg, #059669, #10b981);
                                    color: #fff;
                                    box-shadow: 0 2px 5px rgba(5,150,105,0.3);
                                }
                                .po-action-assign:hover {
                                    background: linear-gradient(135deg, #047857, #059669);
                                    box-shadow: 0 3px 7px rgba(5,150,105,0.4);
                                }
                                .po-action-unassign {
                                    background: #fff7ed;
                                    color: #c2410c;
                                    border: 1px solid #fed7aa;
                                }
                                .po-action-unassign:hover { background: #ffedd5; }
                                .po-action-unassign:disabled { opacity: .55; cursor: wait; }
                            `}</style>
                            <thead>
                                <tr>
                                    <th style={{ width: '60px', whiteSpace: 'nowrap', borderRight: '1px solid rgba(255,255,255,0.2)' }}>Image</th>
                                    <th style={{ whiteSpace: 'nowrap', minWidth: '80px', borderRight: '1px solid rgba(255,255,255,0.2)' }}>Order ID</th>
                                    <th style={{ width: '45px', textAlign: 'center', whiteSpace: 'nowrap', borderRight: '1px solid rgba(255,255,255,0.2)' }}>Qty</th>
                                    <th style={{ whiteSpace: 'nowrap', minWidth: '100px', borderRight: '1px solid rgba(255,255,255,0.2)' }}>Customer</th>
                                    <th style={{ whiteSpace: 'nowrap', minWidth: '90px', borderRight: '1px solid rgba(255,255,255,0.2)' }}>Pickup</th>
                                    <th style={{ whiteSpace: 'nowrap', minWidth: '100px', borderRight: '1px solid rgba(255,255,255,0.2)' }}>Drop-off</th>
                                    <th style={{ whiteSpace: 'nowrap', minWidth: '90px', borderRight: '1px solid rgba(255,255,255,0.2)' }}>Rider</th>
                                    <th style={{ whiteSpace: 'nowrap', minWidth: '70px', borderRight: '1px solid rgba(255,255,255,0.2)' }}>Amount</th>
                                    <th style={{ whiteSpace: 'nowrap', minWidth: '85px', borderRight: '1px solid rgba(255,255,255,0.2)' }}>Created</th>
                                    <th style={{ whiteSpace: 'nowrap', minWidth: '95px', borderRight: '1px solid rgba(255,255,255,0.2)' }}>Assigned At</th>
                                    <th style={{ whiteSpace: 'nowrap', minWidth: '80px', borderRight: '1px solid rgba(255,255,255,0.2)' }}>Status</th>
                                    <th style={{ whiteSpace: 'nowrap', minWidth: '80px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredOrders().map((order) => {
                                    const firstItem = order.orderItems?.[0];
                                    const productImage = firstItem?.image || firstItem?.product?.image || firstItem?.images?.[0] || null;
                                    const deliveryStatus = order.delivery_status || order.assignmentDetails?.status || order.deliveryStatus || 'PENDING';
                                    const statusColor = statusColors[deliveryStatus] || '#9e9e9e';
                                    // Detect order type for a subtle label
                                    const isPackageOrder = !!(order.pickupAddress && !order.orderItems?.length);
                                    const customerInitial = (order.customerName || order.shippingAddress?.fullName || '?').charAt(0).toUpperCase();
                                    // A PENDING_3PL order this partner has not answered is a live offer:
                                    // it can still be accepted or declined, and the clock is running on it.
                                    const partnerResponse = order.assignmentDetails?.partnerResponse;
                                    const isOpenOffer = deliveryStatus === 'PENDING_3PL' && !partnerResponse;
                                    // Putting a rider on an order you have not accepted is a contradiction,
                                    // so assign waits until the offer has actually been taken.
                                    const canAssignRider = deliveryStatus !== 'PENDING_3PL' || partnerResponse === 'ACCEPTED';
                                    const canUnassignRider = canActOnOrders &&
                                        !!order.assignmentDetails?.assignedRiderId &&
                                        ['ASSIGNED', 'ACCEPTED'].includes(deliveryStatus);

                                    return (
                                        <tr key={order._id} className="po-row" style={{ '--status-color': statusColor }}>
                                            {/* Image Cell */}
                                            <td style={{ verticalAlign: 'middle', paddingLeft: '10px' }}>
                                                {productImage ? (
                                                    <img
                                                        src={productImage}
                                                        alt="Product"
                                                        onClick={() => handleImageClick(productImage)}
                                                        style={{
                                                            width: '36px',
                                                            height: '36px',
                                                            objectFit: 'cover',
                                                            borderRadius: '6px',
                                                            border: `2px solid ${statusColor}30`,
                                                            cursor: 'pointer',
                                                            transition: 'transform 0.2s, box-shadow 0.2s',
                                                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                                        }}
                                                        onMouseOver={(e) => { e.target.style.transform = 'scale(1.12)'; e.target.style.boxShadow = '0 4px 10px rgba(0,0,0,0.18)'; }}
                                                        onMouseOut={(e) => { e.target.style.transform = 'scale(1)'; e.target.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)'; }}
                                                    />
                                                ) : (
                                                    <div style={{
                                                        width: '36px',
                                                        height: '36px',
                                                        backgroundColor: `${statusColor}15`,
                                                        borderRadius: '6px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        border: `2px solid ${statusColor}30`,
                                                    }}>
                                                        <MdInventory size={16} color={statusColor} />
                                                    </div>
                                                )}
                                            </td>
                                            {/* Order ID */}
                                            <td style={{ verticalAlign: 'middle' }}>
                                                <div style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    background: '#f1f5f9',
                                                    border: '1px solid #e2e8f0',
                                                    borderRadius: '5px',
                                                    padding: '2px 6px',
                                                    fontFamily: font.mono,
                                                    fontSize: '11px',
                                                    fontWeight: 700,
                                                    color: '#1e293b',
                                                    letterSpacing: '0.03em',
                                                }}>
                                                    #{order._id?.slice(-6).toUpperCase()}
                                                </div>
                                            </td>
                                            {/* Quantity */}
                                            <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                <span style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: '22px',
                                                    height: '22px',
                                                    borderRadius: '50%',
                                                    background: '#f1f5f9',
                                                    border: '1px solid #e2e8f0',
                                                    fontSize: '11px',
                                                    fontWeight: 700,
                                                    color: '#475569',
                                                }}>
                                                    {order.orderItems?.length || 0}
                                                </span>
                                            </td>
                                            {/* Customer */}
                                            <td style={{ verticalAlign: 'middle' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <div style={{
                                                        width: '24px', height: '24px', borderRadius: '50%',
                                                        background: `${statusColor}20`,
                                                        border: `1.5px solid ${statusColor}40`,
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        fontSize: '10px', fontWeight: 700, color: statusColor,
                                                        flexShrink: 0,
                                                    }}>
                                                        {customerInitial}
                                                    </div>
                                                    <div>
                                                        <div style={{ fontSize: '11px', fontWeight: 600, color: '#1e293b', lineHeight: 1.3 }}>
                                                            {order.customerName || order.shippingAddress?.fullName || order.user?.name || 'N/A'}
                                                        </div>
                                                        <div style={{ color: '#64748b', fontSize: '10px' }}>
                                                            {order.shippingAddress?.customerNumber || order.shippingAddress?.mobile || order.user?.phone || ''}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            {/* Pickup */}
                                            <td style={{ verticalAlign: 'middle' }}>
                                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '3px' }}>
                                                    <MdStorefront size={11} style={{ color: '#f59e0b', marginTop: '1px', flexShrink: 0 }} />
                                                    <span style={{ fontSize: '11px', color: '#374151', lineHeight: 1.4 }}>
                                                        {order.pickupAddress?.address ||
                                                            order.orderItems?.[0]?.vendorId?.businessName ||
                                                            order.orderItems?.[0]?.parentRestaurantId?.businessName ||
                                                            order.orderItems?.[0]?.outletId?.name ||
                                                            'N/A'}
                                                    </span>
                                                </div>
                                            </td>
                                            {/* Drop-off */}
                                            <td style={{ verticalAlign: 'middle' }}>
                                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '3px' }}>
                                                    <MdLocationOn size={11} style={{ color: '#ef4444', marginTop: '1px', flexShrink: 0 }} />
                                                    <span style={{ fontSize: '11px', color: '#374151', lineHeight: 1.4 }}>
                                                        {typeof order.shippingAddress?.address === 'object'
                                                            ? order.shippingAddress?.address?.address
                                                            : order.shippingAddress?.address ||
                                                            order.shippingAddress?.locationName?.name ||
                                                            order.shippingAddress?.description ||
                                                            'N/A'}
                                                    </span>
                                                </div>
                                            </td>
                                            {/* Rider */}
                                            <td style={{ verticalAlign: 'middle' }}>
                                                {order.assignmentDetails?.assignedRiderId ? (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <MdTwoWheeler size={12} style={{ color: '#059669', flexShrink: 0 }} />
                                                        <div>
                                                            <div style={{ fontSize: '11px', fontWeight: 600, color: '#1e293b' }}>
                                                                {order.assignmentDetails.assignedRiderId.firstName || 'Assigned'}
                                                            </div>
                                                            <div style={{ color: '#64748b', fontSize: '10px' }}>
                                                                {order.assignmentDetails.assignedRiderId.mobile}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <span style={{
                                                        display: 'inline-flex', alignItems: 'center', gap: '3px',
                                                        fontSize: '10px', fontWeight: 600,
                                                        color: '#64748b', background: '#f1f5f9',
                                                        border: '1px solid #e2e8f0',
                                                        borderRadius: '10px', padding: '2px 7px',
                                                    }}>
                                                        <MdTwoWheeler size={11} /> Unassigned
                                                    </span>
                                                )}
                                            </td>
                                            {/* Amount */}
                                            <td style={{ verticalAlign: 'middle' }}>
                                                <span style={{ fontWeight: 700, color: '#059669', fontSize: '11px' }}>
                                                    {formatCurrency(order.totalPrice || order.deliveryFee?.total || order.shippingPrice)}
                                                </span>
                                            </td>
                                            {/* Created */}
                                            <td style={{ verticalAlign: 'middle' }}>
                                                <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 500 }}>
                                                    {formatDate(order.createdAt)}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="d-flex flex-column align-items-start gap-1">
                                                    {getTimeBadge(
                                                        order.assignmentDetails?.assignedAt,
                                                        order.delivery_status || order.assignmentDetails?.status || order.deliveryStatus
                                                    )}
                                                    {order.assignmentDetails?.assignedAt && (
                                                        <small style={{ fontSize: '12px', color: '#038432ff', fontWeight: 500 }}>
                                                            {formatDate(order.assignmentDetails.assignedAt)}
                                                        </small>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <Chip
                                                    label={order.delivery_status || order.assignmentDetails?.status || order.deliveryStatus || 'PENDING'}
                                                    size="small"
                                                    style={{
                                                        backgroundColor: statusColors[order.delivery_status || order.assignmentDetails?.status || order.deliveryStatus] || '#9e9e9e',
                                                        color: '#fff',
                                                        fontWeight: 600,
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                                        borderRadius: '6px',
                                                    }}
                                                />
                                            </td>
                                            {/* Actions */}
                                            <td className="po-actions-cell" style={{ verticalAlign: 'middle' }}>
                                                <div className="po-actions-stack">
                                                <div>
                                                    <OverlayTrigger placement="top" overlay={<Tooltip>View full order details</Tooltip>}>
                                                        <button
                                                            className="po-action-btn po-action-view"
                                                            onClick={() => handleViewDetails(order)}
                                                        >
                                                            <MdMap size={18} />
                                                            View
                                                        </button>
                                                    </OverlayTrigger>
                                                    {!order.assignmentDetails?.assignedRiderId && canAssignRider && (
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Assign a rider to this order</Tooltip>}>
                                                            <button
                                                                className="po-action-btn po-action-assign"
                                                                onClick={() => handleAssignOrder(order)}
                                                            >
                                                                <MdTwoWheeler size={18} />
                                                                Assign
                                                            </button>
                                                        </OverlayTrigger>
                                                    )}
                                                    {canUnassignRider && (
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Return this order to the dispatch queue</Tooltip>}>
                                                            <button
                                                                className="po-action-btn po-action-unassign"
                                                                disabled={unassignBusyId === order._id}
                                                                onClick={() => askUnassignOrder(order)}
                                                            >
                                                                <MdClose size={18} />
                                                                {unassignBusyId === order._id ? 'Working…' : 'Unassign'}
                                                            </button>
                                                        </OverlayTrigger>
                                                    )}
                                                </div>
                                                {canActOnOrders && isOpenOffer && (
                                                    <div>
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Take this order and start the delivery promise</Tooltip>}>
                                                            <button
                                                                type="button"
                                                                style={button('primary', { padding: '5px 12px', fontSize: 12 })}
                                                                disabled={offerBusyId === order._id}
                                                                onClick={() => handleAcceptOffer(order)}
                                                            >
                                                                Accept
                                                            </button>
                                                        </OverlayTrigger>
                                                        <PartnerHelp topic="acceptOrder" />
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Send this order back to the vendor</Tooltip>}>
                                                            <button
                                                                type="button"
                                                                style={button('danger', { padding: '5px 12px', fontSize: 12 })}
                                                                disabled={offerBusyId === order._id}
                                                                onClick={() => askDeclineOffer(order)}
                                                            >
                                                                Decline
                                                            </button>
                                                        </OverlayTrigger>
                                                        <PartnerHelp topic="declineOrder" />
                                                    </div>
                                                )}
                                                {canActOnOrders && isOpenOffer && renderOfferCountdown(order) && (
                                                    <div>{renderOfferCountdown(order)}</div>
                                                )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                        </div>
                    )}
                </CardBody>
            </Card>

            {/* Order Details Modal */}
            <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
                <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: colors.blue, color: colors.white }}>
                    <Modal.Title>
                        Order Details #{selectedOrder?._id?.slice(-6).toUpperCase()}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ backgroundColor: '#f8f9fa' }}>
                    {selectedOrder && (
                        <>
                            <Row>
                                <Col md="6">
                                    {/* Customer Information Card */}
                                    <div style={{
                                        backgroundColor: 'white',
                                        borderRadius: '12px',
                                        padding: '20px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                        marginBottom: '16px'
                                    }}>
                                        <h6 style={{
                                            color: colors.blue,
                                            fontWeight: 700,
                                            fontSize: '14px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            marginBottom: '16px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}>
                                            <span style={{
                                                width: '28px',
                                                height: '28px',
                                                borderRadius: '6px',
                                                backgroundColor: `${colors.blue}15`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                <MdPerson size={20} color={colors.blue} />
                                            </span>
                                            Customer Information
                                        </h6>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                                <MdPerson size={16} color={colors.muted} />
                                                <div>
                                                    <div style={{ fontSize: '12px', color: colors.muted, marginBottom: '2px' }}>Name</div>
                                                    <div style={{ fontWeight: 600, color: colors.body }}>
                                                        {selectedOrder.customerName || selectedOrder.shippingAddress?.fullName || 'N/A'}
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                                <MdPhone size={16} color={colors.muted} />
                                                <div>
                                                    <div style={{ fontSize: '12px', color: colors.muted, marginBottom: '2px' }}>Phone</div>
                                                    <div style={{ fontWeight: 600, color: colors.body }}>
                                                        {selectedOrder.shippingAddress?.customerNumber || selectedOrder.shippingAddress?.mobile || 'N/A'}
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                                <MdLocationOn size={16} color="#ef4444" />
                                                <div>
                                                    <div style={{ fontSize: '12px', color: colors.muted, marginBottom: '2px' }}>Delivery Address</div>
                                                    <div style={{ fontWeight: 600, color: colors.body }}>
                                                        {selectedOrder.shippingAddress?.address || selectedOrder.shippingAddress?.locationName?.name || 'N/A'}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Delivery Status Card */}
                                    <div style={{
                                        backgroundColor: 'white',
                                        borderRadius: '12px',
                                        padding: '20px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                                    }}>
                                        <h6 style={{
                                            color: colors.green,
                                            fontWeight: 700,
                                            fontSize: '14px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            marginBottom: '16px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}>
                                            <span style={{
                                                width: '28px',
                                                height: '28px',
                                                borderRadius: '6px',
                                                backgroundColor: `${colors.green}15`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                <MdLocalShipping size={20} color={colors.green} />
                                            </span>
                                            Delivery Information
                                        </h6>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <span style={{ fontSize: '13px', color: colors.muted }}>Status</span>
                                                <Chip
                                                    label={selectedOrder.delivery_status || selectedOrder.assignmentDetails?.status || selectedOrder.deliveryStatus || 'PENDING'}
                                                    size="small"
                                                    style={{
                                                        backgroundColor: statusColors[selectedOrder.delivery_status || selectedOrder.assignmentDetails?.status || selectedOrder.deliveryStatus] || '#9e9e9e',
                                                        color: '#fff',
                                                        fontWeight: 700,
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                                        borderRadius: '20px',
                                                        fontSize: '11px',
                                                        padding: '2px 4px'
                                                    }}
                                                />
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <span style={{ fontSize: '13px', color: colors.muted }}>Assigned At</span>
                                                <span style={{ fontWeight: 600, color: colors.body, fontSize: '13px' }}>
                                                    {selectedOrder.assignmentDetails?.assignedAt
                                                        ? formatDate(selectedOrder.assignmentDetails.assignedAt)
                                                        : '—'}
                                                </span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <span style={{ fontSize: '13px', color: colors.muted }}>Assigned Rider</span>
                                                <span style={{
                                                    fontWeight: 600,
                                                    color: selectedOrder.assignmentDetails?.assignedRiderId?.firstName ? colors.green : colors.muted,
                                                    fontSize: '13px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}>
                                                    {selectedOrder.assignmentDetails?.assignedRiderId?.firstName ? (
                                                        <><MdTwoWheeler size={14} style={{ marginRight: 4 }} /> {selectedOrder.assignmentDetails?.assignedRiderId?.firstName}</>
                                                    ) : (
                                                        'Unassigned'
                                                    )}
                                                </span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <span style={{ fontSize: '13px', color: colors.muted }}>Est. Delivery</span>
                                                <span style={{ fontWeight: 600, color: colors.body, fontSize: '13px' }}>
                                                    {selectedOrder.estimatedDeliveryTime || '—'}
                                                </span>
                                            </div>

                                            {(selectedOrder.deliveredAt || selectedOrder.delivery_status === 'DELIVERED' || selectedOrder.assignmentDetails?.status === 'DELIVERED') && (
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <span style={{ fontSize: '13px', color: colors.green }}>Delivered In</span>
                                                    <span style={{ fontWeight: 600, color: colors.green, fontSize: '13px' }}>
                                                        {(() => {
                                                            const startTime = selectedOrder.createdAt;
                                                            const endTime = selectedOrder.deliveredAt || new Date();
                                                            if (!startTime) return '—';
                                                            const diffMs = new Date(endTime) - new Date(startTime);
                                                            const diffMins = Math.floor(diffMs / 60000);
                                                            if (diffMins < 60) return `${diffMins} mins`;
                                                            const hours = Math.floor(diffMins / 60);
                                                            const mins = diffMins % 60;
                                                            return `${hours}h ${mins}m`;
                                                        })()}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Col>
                                <Col md="6">
                                    {/* Order Summary Card */}
                                    <div style={{
                                        backgroundColor: 'white',
                                        borderRadius: '12px',
                                        padding: '20px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                        marginBottom: '16px'
                                    }}>
                                        <h6 style={{
                                            color: '#7c3aed',
                                            fontWeight: 700,
                                            fontSize: '14px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            marginBottom: '16px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}>
                                            <span style={{
                                                width: '28px',
                                                height: '28px',
                                                borderRadius: '6px',
                                                backgroundColor: '#7c3aed15',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                🛒
                                            </span>
                                            Order Summary
                                        </h6>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <span style={{ fontSize: '13px', color: colors.muted }}>Order Date</span>
                                                <span style={{ fontWeight: 600, color: colors.body, fontSize: '13px' }}>
                                                    {formatDate(selectedOrder.createdAt)}
                                                </span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <span style={{ fontSize: '13px', color: colors.muted }}>Total Price</span>
                                                <span style={{ fontWeight: 700, color: colors.green, fontSize: '16px' }}>
                                                    {formatCurrency(selectedOrder.totalPrice)}
                                                </span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <span style={{ fontSize: '13px', color: colors.muted }}>Payment Method</span>
                                                <span style={{
                                                    fontWeight: 600,
                                                    color: colors.body,
                                                    fontSize: '13px',
                                                    textTransform: 'capitalize'
                                                }}>
                                                    {selectedOrder.paymentMethod || '—'}
                                                </span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <span style={{ fontSize: '13px', color: colors.muted }}>Payment Status</span>
                                                <Chip
                                                    label={selectedOrder.isPaid ? '✓ Paid' : 'Pending'}
                                                    size="small"
                                                    style={{
                                                        backgroundColor: selectedOrder.isPaid ? colors.green : colors.amber,
                                                        color: '#fff',
                                                        fontWeight: 600,
                                                        borderRadius: '20px',
                                                        fontSize: '11px'
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Order Items Card */}
                                    {selectedOrder.orderItems && selectedOrder.orderItems.length > 0 && (
                                        <div style={{
                                            backgroundColor: 'white',
                                            borderRadius: '12px',
                                            padding: '20px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                                        }}>
                                            <h6 style={{
                                                color: '#f59e0b',
                                                fontWeight: 700,
                                                fontSize: '14px',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px',
                                                marginBottom: '16px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px'
                                            }}>
                                                <span style={{
                                                    width: '28px',
                                                    height: '28px',
                                                    borderRadius: '6px',
                                                    backgroundColor: '#f59e0b15',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}>
                                                    <MdInventory size={16} color="#dc8b00ff" />
                                                </span>
                                                Items ({selectedOrder.orderItems.length})
                                            </h6>
                                            <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                                                {selectedOrder.orderItems.slice(0, 6).map((item, idx) => {
                                                    const itemImage = item.image || item.product?.image || item.images?.[0] || null;
                                                    return (
                                                        <div
                                                            key={idx}
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '12px',
                                                                marginBottom: '10px',
                                                                padding: '10px',
                                                                backgroundColor: colors.ground,
                                                                borderRadius: '10px',
                                                                border: `1px solid ${colors.lineSoft}`
                                                            }}
                                                        >
                                                            {itemImage ? (
                                                                <img
                                                                    src={itemImage}
                                                                    alt={item.name || 'Product'}
                                                                    style={{
                                                                        width: '48px',
                                                                        height: '48px',
                                                                        objectFit: 'cover',
                                                                        borderRadius: '8px',
                                                                        border: `1px solid ${colors.line}`
                                                                    }}
                                                                />
                                                            ) : (
                                                                <div
                                                                    style={{
                                                                        width: '48px',
                                                                        height: '48px',
                                                                        backgroundColor: colors.lineSoft,
                                                                        borderRadius: '8px',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        border: `1px solid ${colors.line}`
                                                                    }}
                                                                >
                                                                    <span style={{ fontSize: '20px' }}>📦</span>
                                                                </div>
                                                            )}
                                                            <div style={{ flex: 1 }}>
                                                                <div style={{ fontWeight: 600, fontSize: '14px', color: colors.body }}>
                                                                    {item.name || item.product?.name}
                                                                </div>
                                                                <div style={{ fontSize: '12px', color: colors.muted }}>
                                                                    Qty: <strong>{item.qty || 1}</strong>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                {selectedOrder.orderItems.length > 6 && (
                                                    <div style={{
                                                        textAlign: 'center',
                                                        color: colors.muted,
                                                        fontSize: '13px',
                                                        padding: '8px',
                                                        backgroundColor: colors.ground,
                                                        borderRadius: '8px'
                                                    }}>
                                                        +{selectedOrder.orderItems.length - 6} more items
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </Col>
                            </Row>

                            {/* Distance Analysis Section */}
                            <Row className="mt-3">
                                <Col md="6">
                                    {/* Pickup Location Card */}
                                    <div style={{
                                        backgroundColor: 'white',
                                        borderRadius: '12px',
                                        padding: '20px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                        height: '100%'
                                    }}>
                                        <h6 style={{
                                            color: '#00bcd4',
                                            fontWeight: 700,
                                            fontSize: '14px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            marginBottom: '16px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}>
                                            <span style={{
                                                width: '28px',
                                                height: '28px',
                                                borderRadius: '6px',
                                                backgroundColor: '#00bcd415',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                <MdStorefront size={16} />
                                            </span>
                                            Pickup Location
                                        </h6>
                                        {(() => {
                                            const origin = getOriginCoords(selectedOrder);
                                            return (
                                                <div style={{
                                                    padding: '16px',
                                                    backgroundColor: colors.ground,
                                                    borderRadius: '10px',
                                                    border: `1px solid ${colors.lineSoft}`
                                                }}>
                                                    <div style={{ fontWeight: 700, fontSize: '15px', color: colors.body, marginBottom: '6px' }}>
                                                        {origin.name}
                                                    </div>
                                                    <div style={{ fontSize: '13px', color: colors.muted, marginBottom: '12px' }}>
                                                        {origin.address}
                                                    </div>
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                        padding: '8px 12px',
                                                        backgroundColor: 'white',
                                                        borderRadius: '6px',
                                                        border: `1px solid ${colors.line}`,
                                                        fontSize: '12px',
                                                        color: colors.muted
                                                    }}>
                                                        🎯 <strong>Delivery Zone:</strong> {origin.zoneRadius} km
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </Col>
                                <Col md="6">
                                    {/* Distance Analysis Card */}
                                    <div style={{
                                        backgroundColor: 'white',
                                        borderRadius: '12px',
                                        padding: '20px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                        height: '100%'
                                    }}>
                                        <h6 style={{
                                            color: '#3f51b5',
                                            fontWeight: 700,
                                            fontSize: '14px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            marginBottom: '16px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}>
                                            <span style={{
                                                width: '28px',
                                                height: '28px',
                                                borderRadius: '6px',
                                                backgroundColor: '#3f51b515',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                <MdDirections size={16} />
                                            </span>
                                            Distance Analysis
                                        </h6>
                                        {(() => {
                                            const origin = getOriginCoords(selectedOrder);
                                            const customerLat = selectedOrder?.shippingAddress?.latLng?.lat;
                                            const customerLng = selectedOrder?.shippingAddress?.latLng?.lng;

                                            if (!origin.lat || !origin.lng || !customerLat || !customerLng) {
                                                return (
                                                    <div style={{
                                                        padding: '16px',
                                                        backgroundColor: colors.ground,
                                                        borderRadius: '10px',
                                                        textAlign: 'center',
                                                        color: colors.muted,
                                                        fontSize: '13px'
                                                    }}>
                                                        📍 Location data unavailable for distance calculation
                                                    </div>
                                                );
                                            }

                                            const distance = calculateDistance(origin.lat, origin.lng, customerLat, customerLng);
                                            const isOutsideZone = parseFloat(distance) > origin.zoneRadius;

                                            return (
                                                <div style={{
                                                    padding: '16px',
                                                    backgroundColor: colors.ground,
                                                    borderRadius: '10px',
                                                    border: `1px solid ${colors.lineSoft}`
                                                }}>
                                                    <div style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        marginBottom: '14px'
                                                    }}>
                                                        <div>
                                                            <div style={{ fontSize: '24px', fontWeight: 700, color: colors.body }}>
                                                                {distance} km
                                                            </div>
                                                            <div style={{ fontSize: '12px', color: colors.muted }}>
                                                                to customer
                                                            </div>
                                                        </div>
                                                        <Chip
                                                            label={isOutsideZone ? '⚠️ Outside Zone' : '✓ Within Zone'}
                                                            style={{
                                                                backgroundColor: isOutsideZone ? colors.red : colors.green,
                                                                color: 'white',
                                                                fontSize: '11px',
                                                                fontWeight: 600,
                                                                borderRadius: '20px'
                                                            }}
                                                        />
                                                    </div>
                                                    {isOutsideZone && (
                                                        <Alert variant="warning" style={{
                                                            padding: '10px 12px',
                                                            marginBottom: '14px',
                                                            fontSize: '12px',
                                                            borderRadius: '8px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '6px'
                                                        }}>
                                                            <MdWarning size={16} />
                                                            Customer is {(parseFloat(distance) - origin.zoneRadius).toFixed(2)} km outside the zone
                                                        </Alert>
                                                    )}
                                                    <Button
                                                        variant="primary"
                                                        size="sm"
                                                        onClick={() => setShowMapModal(true)}
                                                        style={{
                                                            width: '100%',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            gap: '8px',
                                                            padding: '10px',
                                                            borderRadius: '8px',
                                                            fontWeight: 600
                                                        }}
                                                    >
                                                        <MdMap size={18} />
                                                        View Route on Map
                                                    </Button>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </Col>
                            </Row>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer style={{ backgroundColor: '#f8f9fa', borderTop: 'none' }}>
                    <Button
                        variant="outline-secondary"
                        onClick={() => setShowDetailsModal(false)}
                        style={{ borderRadius: '8px', fontWeight: 600, padding: '8px 20px' }}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Assign Rider Modal */}
            <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)} size="lg">
                <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: colors.green, color: colors.white }}>
                    <Modal.Title>Assign Rider</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ marginBottom: '4px' }}>Select a rider to assign to order <strong>#{selectedOrder?._id?.slice(-6).toUpperCase()}</strong></p>
                    {/* Show vendor location context */}
                    {selectedOrder && (() => {
                        const origin = getOriginCoords(selectedOrder);
                        return origin.name !== 'N/A' ? (
                            <p style={{ fontSize: '12px', color: colors.muted, margin: '0 0 12px 0' }}>
                                📦 Pickup from: <strong>{origin.name}</strong>{origin.address !== 'N/A' && origin.address !== origin.name ? ` — ${origin.address}` : ''}
                            </p>
                        ) : null;
                    })()}

                    {/* Search input */}
                    <div style={{ position: 'relative', marginBottom: '12px' }}>
                        <input
                            type="text"
                            placeholder="Search riders by name..."
                            value={riderSearch}
                            onChange={(e) => setRiderSearch(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '10px 14px 10px 36px',
                                border: '1px solid #e5e7eb',
                                borderRadius: '10px',
                                fontSize: '13px',
                                outline: 'none',
                                backgroundColor: '#f9fafb',
                                transition: 'border-color 0.2s, box-shadow 0.2s',
                                boxSizing: 'border-box'
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = colors.green;
                                e.target.style.boxShadow = '0 0 0 3px rgba(0, 98, 2, 0.1)';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = '#e5e7eb';
                                e.target.style.boxShadow = 'none';
                            }}
                        />
                        <span style={{
                            position: 'absolute',
                            left: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            fontSize: '14px',
                            color: colors.muted,
                            pointerEvents: 'none'
                        }}>🔍</span>
                        {riderSearch && (
                            <button
                                onClick={() => setRiderSearch('')}
                                style={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    color: colors.muted,
                                    padding: '2px 4px',
                                    borderRadius: '4px',
                                    lineHeight: 1
                                }}
                                tabIndex={-1}
                            >
                                ✕
                            </button>
                        )}
                    </div>

                    {/* Rider list with proximity */}
                    {(() => {
                        let activeRiders = riders.filter(r => r.activeStatus === 'Active');

                        // Apply search filter
                        if (riderSearch.trim()) {
                            const query = riderSearch.trim().toLowerCase();
                            activeRiders = activeRiders.filter(r =>
                                (r.firstName || '').toLowerCase().includes(query) ||
                                (r.lastName || '').toLowerCase().includes(query) ||
                                (`${r.firstName || ''} ${r.lastName || ''}`).toLowerCase().includes(query)
                            );
                        }

                        if (riders.filter(r => r.activeStatus === 'Active').length === 0) {
                            return (
                                <Alert variant="warning">
                                    No active riders available. Please add riders first.
                                </Alert>
                            );
                        }

                        if (activeRiders.length === 0 && riderSearch.trim()) {
                            return (
                                <div style={{
                                    textAlign: 'center',
                                    padding: '30px 20px',
                                    color: colors.muted
                                }}>
                                    <div style={{ fontSize: '28px', marginBottom: '8px' }}>🔍</div>
                                    <div style={{ fontSize: '14px', fontWeight: 500 }}>No riders match "{riderSearch}"</div>
                                    <div style={{ fontSize: '12px', marginTop: '4px' }}>Try a different name</div>
                                </div>
                            );
                        }

                        // Calculate distances and sort: online first, then by distance
                        const ridersWithDistance = activeRiders.map(rider => {
                            const dist = selectedOrder ? getRiderDistanceFromOrder(rider, selectedOrder) : null;
                            return { rider, distance: dist !== null ? parseFloat(dist) : null };
                        });

                        ridersWithDistance.sort((a, b) => {
                            // Online first
                            const aOnline = a.rider.onlineStatus === 'Online' ? 1 : 0;
                            const bOnline = b.rider.onlineStatus === 'Online' ? 1 : 0;
                            if (bOnline !== aOnline) return bOnline - aOnline;
                            // Then by distance (null/unknown last)
                            if (a.distance === null && b.distance === null) return 0;
                            if (a.distance === null) return 1;
                            if (b.distance === null) return -1;
                            return a.distance - b.distance;
                        });

                        // Find the closest online rider for recommendation
                        const closestOnline = ridersWithDistance.find(
                            r => r.rider.onlineStatus === 'Online' && r.distance !== null && !r.rider.codCash?.isFlagged
                        );

                        return (
                            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                {ridersWithDistance.map(({ rider, distance }, idx) => {
                                    const isOnline = rider.onlineStatus === 'Online';
                                    const isCodFlagged = rider.codCash?.isFlagged === true;
                                    const isSelected = selectedRider === rider._id;
                                    const isClosest = closestOnline && closestOnline.rider._id === rider._id && !isCodFlagged;

                                    let distColor = colors.muted;
                                    let distBg = colors.ground;
                                    if (distance !== null) {
                                        if (distance <= 2) { distColor = '#16a34a'; distBg = 'rgba(34,197,94,0.1)'; }
                                        else if (distance <= 5) { distColor = '#2563eb'; distBg = 'rgba(37,99,235,0.08)'; }
                                        else if (distance <= 10) { distColor = '#d97706'; distBg = 'rgba(245,158,11,0.08)'; }
                                        else { distColor = '#dc2626'; distBg = 'rgba(220,38,38,0.08)'; }
                                    }

                                    const locationAge = rider.currentLocation?.lastUpdated
                                        ? Math.floor((Date.now() - new Date(rider.currentLocation.lastUpdated).getTime()) / 60000)
                                        : null;

                                    return (
                                        <div
                                            key={rider._id}
                                            onClick={() => {
                                                setSelectedRider(rider._id);
                                                setForceAssign(isCodFlagged);
                                            }}
                                            title={isCodFlagged ? `Rider has UGX ${(rider.codCash?.heldBalance || 0).toLocaleString()} in held COD cash — cannot receive orders until they deposit` : undefined}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px',
                                                padding: '12px 14px',
                                                marginBottom: '8px',
                                                borderRadius: '10px',
                                                cursor: 'pointer',
                                                border: isCodFlagged
                                                    ? '1px solid #fecaca'
                                                    : isSelected
                                                        ? `2px solid ${colors.green}`
                                                        : `1px solid ${isOnline ? '#e5e7eb' : '#f3f4f6'}`,
                                                backgroundColor: isCodFlagged
                                                    ? '#fef2f2'
                                                    : isSelected
                                                        ? 'rgba(0, 98, 2, 0.05)'
                                                        : isOnline ? '#fff' : '#fafafa',
                                                transition: 'all 0.15s ease',
                                                opacity: isCodFlagged ? 0.6 : isOnline ? 1 : 0.7,
                                                position: 'relative'
                                            }}
                                        >
                                            {/* Recommended badge */}
                                            {isClosest && (
                                                <span style={{
                                                    position: 'absolute',
                                                    top: '-8px',
                                                    right: '10px',
                                                    fontSize: '10px',
                                                    fontWeight: 700,
                                                    color: '#fff',
                                                    backgroundColor: '#16a34a',
                                                    padding: '2px 8px',
                                                    borderRadius: '10px',
                                                    letterSpacing: '0.3px'
                                                }}>
                                                    ⚡ CLOSEST
                                                </span>
                                            )}

                                            {/* Selection indicator */}
                                            <div style={{
                                                width: '20px',
                                                height: '20px',
                                                borderRadius: '50%',
                                                border: isSelected
                                                    ? `6px solid ${colors.green}`
                                                    : `2px solid ${colors.line}`,
                                                flexShrink: 0,
                                                transition: 'all 0.15s ease'
                                            }} />

                                            {/* Avatar */}
                                            <div style={{ position: 'relative', flexShrink: 0 }}>
                                                <div style={{
                                                    width: '38px',
                                                    height: '38px',
                                                    borderRadius: '50%',
                                                    backgroundColor: isOnline ? '#dcfce7' : colors.lineSoft,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontWeight: 600,
                                                    fontSize: '15px',
                                                    color: isOnline ? '#16a34a' : colors.muted
                                                }}>
                                                    {rider.firstName?.charAt(0)?.toUpperCase() || 'R'}
                                                </div>
                                                <span style={{
                                                    position: 'absolute',
                                                    bottom: 0,
                                                    right: 0,
                                                    width: '10px',
                                                    height: '10px',
                                                    borderRadius: '50%',
                                                    backgroundColor: isOnline ? '#22c55e' : '#9ca3af',
                                                    border: '2px solid #fff'
                                                }} />
                                            </div>

                                            {/* Rider info */}
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <span style={{
                                                        fontWeight: 600,
                                                        fontSize: '13px',
                                                        color: colors.body
                                                    }}>
                                                        {rider.firstName} {rider.lastName || ''}
                                                    </span>
                                                    <Badge
                                                        bg={isOnline ? 'success' : 'secondary'}
                                                        style={{ fontSize: '9px', padding: '2px 6px' }}
                                                    >
                                                        {rider.onlineStatus}
                                                    </Badge>
                                                    {rider.vehicleDetails?.vehicleType && (
                                                        <span style={{
                                                            fontSize: '10px',
                                                            color: colors.muted,
                                                            backgroundColor: colors.ground,
                                                            padding: '1px 6px',
                                                            borderRadius: '8px'
                                                        }}>
                                                            {rider.vehicleDetails.vehicleType}
                                                        </span>
                                                    )}
                                                    {isCodFlagged && (
                                                        <Badge
                                                            bg="danger"
                                                            style={{ fontSize: '9px', padding: '2px 6px' }}
                                                        >
                                                            COD Limit
                                                        </Badge>
                                                    )}
                                                </div>
                                                {isCodFlagged && (
                                                    <div style={{ fontSize: '10px', color: '#dc2626', marginTop: '2px' }}>
                                                        Held: UGX {(rider.codCash?.heldBalance || 0).toLocaleString()} — must deposit first
                                                    </div>
                                                )}
                                                {/* Location age */}
                                                {locationAge !== null && locationAge < 60 && (
                                                    <div style={{ fontSize: '10px', color: colors.muted, marginTop: '2px' }}>
                                                        Location updated {locationAge < 1 ? 'just now' : `${locationAge}m ago`}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Distance badge */}
                                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                                {distance !== null ? (
                                                    <div style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'flex-end',
                                                        gap: '2px'
                                                    }}>
                                                        <span style={{
                                                            fontSize: '14px',
                                                            fontWeight: 700,
                                                            color: distColor,
                                                            backgroundColor: distBg,
                                                            padding: '3px 10px',
                                                            borderRadius: '12px',
                                                            lineHeight: 1.2
                                                        }}>
                                                            {distance < 1 ? `${Math.round(distance * 1000)}m` : `${distance.toFixed(1)} km`}
                                                        </span>
                                                        <span style={{ fontSize: '9px', color: colors.muted }}>from pickup</span>
                                                    </div>
                                                ) : (
                                                    <span style={{
                                                        fontSize: '11px',
                                                        color: colors.muted,
                                                        fontStyle: 'italic'
                                                    }}>
                                                        No location
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })()}
                </Modal.Body>
                {forceAssign && selectedRider && (
                    <div style={{
                        margin: '0 16px 0',
                        padding: '10px 14px',
                        backgroundColor: '#fef3c7',
                        border: '1px solid #f59e0b',
                        borderRadius: '8px',
                        fontSize: '12px',
                        color: '#92400e',
                        lineHeight: 1.5
                    }}>
                        ⚠️ <strong>COD Limit Override:</strong> This rider has exceeded their COD cash threshold.
                        Assigning anyway may increase uncollected cash risk. Proceed only if you have confirmed the situation with the rider.
                    </div>
                )}
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAssignModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        style={{
                            backgroundColor: forceAssign ? '#d97706' : colors.green,
                            borderColor: forceAssign ? '#d97706' : colors.green
                        }}
                        onClick={handleAssignSubmit}
                        disabled={assigning || !selectedRider}
                    >
                        {assigning ? (
                            <>
                                <Spinner size="sm" animation="border" className="me-2" />
                                Assigning...
                            </>
                        ) : forceAssign ? (
                            '⚠️ Override & Assign'
                        ) : (
                            'Assign Rider'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Image Preview Modal */}
            <Modal
                show={showImagePreview}
                onHide={() => setShowImagePreview(false)}
                centered
                size="lg"
            >
                <Modal.Header closeButton closeVariant="white" style={{ border: 'none', position: 'absolute', right: 0, zIndex: 1 }}>
                </Modal.Header>
                <Modal.Body style={{ padding: 0, backgroundColor: 'transparent' }}>
                    {previewImage && (
                        <img
                            src={previewImage}
                            alt="Product Preview"
                            style={{
                                width: '100%',
                                maxHeight: '80vh',
                                objectFit: 'contain',
                                borderRadius: '8px'
                            }}
                        />
                    )}
                </Modal.Body>
            </Modal>

            {/* Map Modal */}
            <Modal
                show={showMapModal}
                onHide={() => setShowMapModal(false)}
                centered
                size="lg"
            >
                <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: colors.blue, color: colors.white }}>
                    <Modal.Title style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <MdMap size={24} />
                        Delivery Route
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: 0 }}>
                    {selectedOrder && (() => {
                        const origin = getOriginCoords(selectedOrder);
                        const customerLat = selectedOrder?.shippingAddress?.latLng?.lat;
                        const customerLng = selectedOrder?.shippingAddress?.latLng?.lng;

                        if (!origin.lat || !origin.lng || !customerLat || !customerLng) {
                            return (
                                <Alert variant="warning" style={{ margin: '1rem' }}>
                                    Location coordinates are missing. Cannot display map.
                                </Alert>
                            );
                        }

                        const mapUrl = `https://www.google.com/maps/embed/v1/directions?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&origin=${origin.lat},${origin.lng}&destination=${customerLat},${customerLng}&mode=driving`;

                        return (
                            <iframe
                                title="Delivery Route"
                                width="100%"
                                height="450"
                                style={{ border: 0 }}
                                loading="lazy"
                                allowFullScreen
                                src={mapUrl}
                            />
                        );
                    })()}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowMapModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* One dialog for the page, driven by whichever row asked. */}
            <ConfirmDialog {...confirmDialog.props} />
        </div>
    );
};

export default PartnerOrders;
